import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import api from '../api';
import { Game, BaseballScore, SoccerScore, SoccerEvent, BasketballScore, BasketballStatus, GameVideo, RunnerInfo } from '../types';
import VideoPlayer from '../components/VideoPlayer';
import { FieldSponsorBug, LowerThirdSponsor, SeasonSponsorCard, useSponsorPlacements } from '../components/SponsorComponents';
import BaseballBoxScore from '../components/BoxScore/BaseballBoxScore';
import SoccerBoxScore from '../components/BoxScore/SoccerBoxScore';
import BasketballBoxScore from '../components/BoxScore/BasketballBoxScore';
import { mediaUrl } from '../utils/mediaUrl';
import { parseGameTime } from '../utils/gameTime';

const SPORT_ICONS: Record<string, string> = { baseball: '⚾', softball: '🥎', soccer: '⚽', basketball: '🏀' };
// ── Timer helpers ─────────────────────────────────────────────────────────────
function computeElapsedMs(score: SoccerScore): number {
  const base = score.timer_elapsed || 0;
  const running = score.timer_running === 1;
  const elapsed = base + (running ? Date.now() - (score.timer_start || 0) : 0);
  // No upper cap — the clock runs until the admin manually stops the half,
  // so injury time, extra time, etc. all display correctly.
  return Math.max(0, elapsed);
}

function formatTimer(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const PERIOD_LABELS: Record<string, string> = {
  not_started:       'Pre-Game',
  first_half:        '1st Half',
  half_time:         'Half Time',
  second_half:       '2nd Half',
  full_time:         'Full Time',
  extra_time:        'ET 1st Half',
  extra_time_half:   'ET Half Time',
  extra_time_second: 'ET 2nd Half',
  extra_time_full:   'ET Full Time',
  penalties:         'Penalty Kicks',
};

const TIMER_ACTIVE_STATUSES = new Set(['first_half', 'second_half', 'extra_time', 'extra_time_second']);

// ── Helper: abbreviate a team name for the score bug ─────────────────────────
function teamAbbr(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2 && name.length > 9) return words[0].slice(0, 6).toUpperCase();
  return name.slice(0, 6).toUpperCase();
}

// ── ESPN-style corner score bug (top-left overlay on video) ──────────────────
interface ScoreBugProps {
  homeTeam: string; awayTeam: string;
  homeLogo: string | null | undefined; awayLogo: string | null | undefined;
  homeScore: number; awayScore: number;
  status: string; timerMs: number;
  sponsor?: { id: string; name: string; logo_url?: string | null } | null;
}
const ScoreBug: React.FC<ScoreBugProps> = ({
  homeTeam, awayTeam, homeLogo, awayLogo,
  homeScore, awayScore, status, timerMs, sponsor,
}) => {
  const isActive = TIMER_ACTIVE_STATUSES.has(status);
  const periodShort: Record<string, string> = {
    first_half: '1ST', half_time: 'HT', second_half: '2ND',
    full_time: 'FT', extra_time: 'ET1', extra_time_half: 'ETHT',
    extra_time_second: 'ET2', extra_time_full: 'ETFT', penalties: 'PKS',
    not_started: 'PRE',
  };
  const period = periodShort[status] ?? status.toUpperCase().slice(0, 4);

  const teamRow = (
    name: string,
    logo: string | null | undefined,
    score: number,
    isBottom: boolean,
  ) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: isBottom ? '4px 10px 6px' : '6px 10px 4px',
      borderBottom: isBottom ? 'none' : '1px solid rgba(255,255,255,0.07)',
    }}>
      {logo
        ? <img src={logo} alt="" style={{ width: 17, height: 17, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }} />
        : <div style={{ width: 17, height: 17, borderRadius: 2, background: 'rgba(255,255,255,0.12)', flexShrink: 0 }} />
      }
      <span style={{
        flex: 1, fontSize: 11, fontWeight: 800, color: '#E8F0FE',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        maxWidth: 68,
      }}>
        {teamAbbr(name)}
      </span>
      <span style={{ fontSize: 19, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums', lineHeight: 1, minWidth: 20, textAlign: 'right' }}>
        {score}
      </span>
    </div>
  );

  return (
    <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 20, pointerEvents: 'none', userSelect: 'none' }}>
      <div style={{
        background:     'rgba(6,8,14,0.88)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius:   8,
        border:         '1px solid rgba(255,255,255,0.09)',
        overflow:       'hidden',
        minWidth:       130,
        boxShadow:      '0 4px 20px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.4)',
      }}>
        {/* Home row */}
        {teamRow(homeTeam, homeLogo ?? null, homeScore, false)}
        {/* Away row */}
        {teamRow(awayTeam, awayLogo ?? null, awayScore, true)}

        {/* Period / clock strip */}
        <div style={{
          borderTop:  '1px solid rgba(255,255,255,0.07)',
          background: 'rgba(255,255,255,0.04)',
          padding:    '4px 10px',
          display:    'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: '#F97316', letterSpacing: '0.8px' }}>{period}</span>
          {isActive && (
            <>
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>·</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                {formatTimer(timerMs)}
              </span>
            </>
          )}
        </div>

        {/* Sponsor strip — optional */}
        {sponsor && (
          <div style={{
            borderTop:  '1px solid rgba(255,255,255,0.07)',
            padding:    '3px 10px',
            display:    'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}>
            <span style={{ fontSize: 7, color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.7px', whiteSpace: 'nowrap' }}>
              Presented by
            </span>
            {sponsor.logo_url
              ? <img
                  src={mediaUrl(sponsor.logo_url)}
                  alt={sponsor.name}
                  style={{ height: 14, maxWidth: 60, objectFit: 'contain' }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              : <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>{sponsor.name}</span>
            }
          </div>
        )}
      </div>
    </div>
  );
};

// ── Scrolling stats ticker (bottom of video) ──────────────────────────────────
const EventTicker: React.FC<{
  events: SoccerEvent[];
  homeTeam: string;
  awayTeam: string;
  /** Admin-authored custom messages, shown first in the ticker */
  customItems?: string[];
}> = ({ events, homeTeam, awayTeam, customItems = [] }) => {
  const statItems = events
    .filter(e => e.event_type === 'goal' || e.event_type === 'assist')
    .sort((a, b) => a.minute - b.minute)
    .map(e => {
      const teamName = e.team === 'home' ? homeTeam : awayTeam;
      const icon  = e.event_type === 'goal' ? '⚽' : '🎯';
      const label = e.event_type === 'goal' ? 'Goal' : 'Assist';
      const name  = e.player_name ? e.player_name : label;
      return `${icon}  ${e.minute}'  ${name}  (${teamName})`;
    });

  // Custom messages appear first (marked with 📢), then goal/assist stats
  const items = [
    ...customItems.map(t => `📢  ${t}`),
    ...statItems,
  ];

  if (!items.length) return null;

  // Duplicate content for seamless loop; duration scales with item count
  const content = items.join('          ·          ');
  const durationSec = Math.max(16, items.length * 7);

  return (
    <div style={{
      position:  'absolute',
      bottom:    52,          // leave room for native video controls bar
      left:      0,
      right:     0,
      zIndex:    20,
      pointerEvents: 'none',
      overflow:  'hidden',
    }}>
      <div style={{
        background:  'rgba(5,7,12,0.84)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderTop:   '1px solid rgba(249,115,22,0.4)',
        display:     'flex',
        alignItems:  'center',
      }}>
        {/* "STATS" label */}
        <div style={{
          background:    '#F97316',
          padding:       '5px 9px',
          fontSize:      9,
          fontWeight:    900,
          letterSpacing: '1.2px',
          color:         '#fff',
          textTransform: 'uppercase',
          whiteSpace:    'nowrap',
          flexShrink:    0,
          alignSelf:     'stretch',
          display:       'flex',
          alignItems:    'center',
        }}>
          STATS
        </div>

        {/* Scrolling area — two copies of content for seamless loop */}
        <div style={{ overflow: 'hidden', flex: 1 }}>
          <div style={{
            display:     'inline-block',
            whiteSpace:  'nowrap',
            fontSize:    11,
            fontWeight:  600,
            color:       '#E8F0FE',
            lineHeight:  '28px',
            animation:   `ticker-scroll ${durationSec}s linear infinite`,
          }}>
            <span style={{ paddingRight: 60 }}>{content}</span>
            <span style={{ paddingRight: 60 }}>{content}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Top-right corner logo / ad bug ────────────────────────────────────────────
// Mirror of the ScoreBug position but for a single logo (team badge or sponsor ad).
// Width/height are sized to roughly match the visual weight of the ScoreBug.
const CornerLogoBug: React.FC<{ logoUrl: string }> = ({ logoUrl }) => (
  <div style={{
    position: 'absolute', top: 10, right: 10, zIndex: 20,
    pointerEvents: 'none', userSelect: 'none',
  }}>
    <div style={{
      background:           'rgba(6,8,14,0.85)',
      backdropFilter:       'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      borderRadius:         8,
      border:               '1px solid rgba(255,255,255,0.09)',
      overflow:             'hidden',
      width:                100,
      height:               68,
      display:              'flex',
      alignItems:           'center',
      justifyContent:       'center',
      padding:              8,
      boxShadow:            '0 4px 20px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.4)',
    }}>
      <img
        src={mediaUrl(logoUrl)}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    </div>
  </div>
);

// ── Soccer score overlay on the video (ESPN bottom-bar style) ────────────────
const SoccerOverlay = ({ score, homeTeam, awayTeam, timerMs }: {
  score: SoccerScore; homeTeam: string; awayTeam: string; timerMs: number;
}) => {
  const isActive = TIMER_ACTIVE_STATUSES.has(score.status);
  const period   = PERIOD_LABELS[score.status] ?? score.status;
  const minutes  = Math.floor(timerMs / 60000);

  return (
    <div style={{
      width: '100%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '6px 12px',
      pointerEvents: 'none',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        background: 'rgba(11,14,19,0.88)', backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
        overflow: 'hidden', height: '44px',
      }}>
        {/* Away team */}
        <div style={{ padding: '0 14px', display: 'flex', alignItems: 'center', gap: '10px', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#F0F6FC', whiteSpace: 'nowrap', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{awayTeam}</span>
          <span style={{ fontSize: '22px', fontWeight: 900, color: '#fff', lineHeight: 1, minWidth: '20px', textAlign: 'center' }}>{score.away_score}</span>
        </div>

        {/* Centre: period + time */}
        <div style={{ padding: '0 14px', textAlign: 'center', minWidth: '90px' }}>
          {score.status === 'not_started' ? (
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#8892A4', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pre-Game</span>
          ) : (
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1 }}>{period}</div>
              {isActive && (
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#fff', lineHeight: 1.4 }}>{minutes}'</div>
              )}
            </div>
          )}
        </div>

        {/* Home team */}
        <div style={{ padding: '0 14px', display: 'flex', alignItems: 'center', gap: '10px', borderLeft: '1px solid rgba(255,255,255,0.08)' }}>
          <span style={{ fontSize: '22px', fontWeight: 900, color: '#fff', lineHeight: 1, minWidth: '20px', textAlign: 'center' }}>{score.home_score}</span>
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#F0F6FC', whiteSpace: 'nowrap', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{homeTeam}</span>
        </div>
      </div>
    </div>
  );
};

// ── ESPN-style baseball score overlay (bottom-right) ─────────────────────────
const BaseballOverlay = ({ score, homeTeam, awayTeam }: {
  score: BaseballScore; homeTeam: string; awayTeam: string;
}) => {
  const isTop = !!score.is_top_inning;
  return (
    <div style={{
      background: 'rgba(8,10,16,0.88)', backdropFilter: 'blur(6px)',
      border: '1px solid rgba(249,115,22,0.25)', borderRadius: '10px',
      padding: '8px 12px', minWidth: '140px', fontFamily: 'inherit',
    }}>
      <div style={{ fontSize: '12px', fontWeight: 900, color: '#F97316', marginBottom: '6px', textAlign: 'center' }}>
        {isTop ? '▲' : '▼'} {score.current_inning}
        <span style={{ fontSize: '10px', fontWeight: 600, color: '#4A5568', marginLeft: '8px' }}>
          {score.outs} out{score.outs !== 1 ? 's' : ''}
        </span>
      </div>
      {[
        { name: awayTeam, runs: score.away_total_runs, batting: isTop },
        { name: homeTeam, runs: score.home_total_runs, batting: !isTop },
      ].map(({ name, runs, batting }) => (
        <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
          <span style={{ fontSize: '10px', color: batting ? '#F97316' : 'rgba(255,255,255,0.55)', fontWeight: batting ? 800 : 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {batting ? '● ' : ''}{name}
          </span>
          <span style={{ fontSize: '20px', fontWeight: 900, color: '#fff', lineHeight: 1, minWidth: '22px', textAlign: 'right' }}>{runs}</span>
        </div>
      ))}
    </div>
  );
};

// ── Unified Soccer Match Control (timer + scoring in one panel) ──────────────
interface TeamPlayer { id: string; name: string; number: number | null; }

const SoccerMatchControl = ({ gameId, score, timerMs, homeTeam, awayTeam, teamPlayers, ourSide, onSetOurSide, onUpdate, onCompleteGame }: {
  gameId: string; score: SoccerScore; timerMs: number; homeTeam: string; awayTeam: string;
  teamPlayers: TeamPlayer[]; ourSide: 'home' | 'away' | null;
  onSetOurSide: (side: 'home' | 'away') => void;
  onUpdate: (s: SoccerScore) => void;
  onCompleteGame: () => void;
}) => {
  type Phase = 'idle' | 'goal-our' | 'goal-opp' | 'ask-assist' | 'assist-our';

  const [phase,     setPhase]     = useState<Phase>('idle');
  const [scorerId,  setScorerId]  = useState('');
  const [assistId,  setAssistId]  = useState('');
  const [oppNumber, setOppNumber] = useState('');
  const [minute,    setMinute]    = useState('');
  const [busy,      setBusy]      = useState(false);
  const [timerBusy, setTimerBusy] = useState(false);
  const [flashTeam, setFlashTeam] = useState<'home'|'away'|null>(null);

  // ── Derived state ──────────────────────────────────────────────────────────
  const st          = score.status;
  const isRunning   = score.timer_running === 1;
  const isTimerActive = TIMER_ACTIVE_STATUSES.has(st);
  const canScore    = isTimerActive || ['half_time', 'extra_time_half'].includes(st);

  // Regulation half = 45 min; ET halves = 15 min each.
  // These offsets reflect when each period starts on the game clock.
  const periodOffset =
    st === 'second_half'       ? 45 :
    st === 'extra_time'        ? 90 :
    st === 'extra_time_second' ? 105 : 0;
  const currentMinute = Math.max(1, Math.floor(timerMs / 60000) + periodOffset);

  const ourTeamSide = ourSide ?? 'home';
  const oppTeamSide = ourTeamSide === 'home' ? 'away' : 'home';
  const ourTeamName = ourTeamSide === 'home' ? homeTeam : awayTeam;
  const oppTeamName = oppTeamSide === 'home' ? homeTeam : awayTeam;
  const sortedPlayers = [...teamPlayers].sort((a, b) => (a.number ?? 99) - (b.number ?? 99));

  // ── Timer actions ──────────────────────────────────────────────────────────
  const timerAction = useCallback(async (action: string, status?: string, reset?: boolean) => {
    setTimerBusy(true);
    try {
      const res = await api.put(`/scores/soccer/${gameId}/timer`, { action, status, reset });
      onUpdate(res.data);
    } catch {}
    finally { setTimerBusy(false); }
  }, [gameId, onUpdate]);

  // Context-aware primary timer button — one clear next action at all times
  type TimerBtn = { label: string; color: string; fn: () => void };
  const primaryBtn: TimerBtn | null = ((): TimerBtn | null => {
    if (st === 'not_started')       return { label: '▶  Kick Off — Start 1st Half', color: '#22C55E', fn: () => timerAction('start', 'first_half', true) };
    if (st === 'first_half')        return { label: '⏸  End 1st Half',              color: '#EAB308', fn: () => timerAction('pause', 'half_time') };
    if (st === 'half_time')         return { label: '▶  Start 2nd Half',             color: '#22C55E', fn: () => timerAction('start', 'second_half', true) };
    if (st === 'second_half')       return { label: '⏹  Full Time',                 color: '#EF4444', fn: () => timerAction('pause', 'full_time') };
    if (st === 'full_time')         return { label: '▶  Start Overtime',             color: '#A78BFA', fn: () => timerAction('start', 'extra_time', true) };
    if (st === 'extra_time')        return { label: '⏸  OT Half Time',              color: '#EAB308', fn: () => timerAction('pause', 'extra_time_half') };
    if (st === 'extra_time_half')   return { label: '▶  OT 2nd Half',               color: '#22C55E', fn: () => timerAction('start', 'extra_time_second', true) };
    if (st === 'extra_time_second') return { label: '⏹  OT Full Time',              color: '#EF4444', fn: () => timerAction('pause', 'extra_time_full') };
    return null;
  })();

  // ── Goal / event actions ───────────────────────────────────────────────────
  const resetForm = () => { setPhase('idle'); setScorerId(''); setAssistId(''); setOppNumber(''); setMinute(''); };
  const flash = (team: 'home' | 'away') => { setFlashTeam(team); setTimeout(() => setFlashTeam(null), 1200); };

  const openOurGoal = () => { setPhase('goal-our'); setScorerId(''); setMinute(String(currentMinute)); };
  const openOppGoal = () => { setPhase('goal-opp'); setOppNumber(''); setMinute(String(currentMinute)); };

  const submitOurGoal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const player = sortedPlayers.find(p => p.id === scorerId);
      const res = await api.post(`/scores/soccer/${gameId}/event`, {
        event_type: 'goal', team: ourTeamSide,
        player_name: player?.name ?? '', player_id: scorerId || null,
        minute: parseInt(minute) || currentMinute,
      });
      onUpdate(res.data);  // immediate update — no waiting for socket round-trip
      flash(ourTeamSide);
      setPhase('ask-assist');
    } catch (err: any) { alert(err?.response?.data?.error || 'Failed to log goal.'); }
    finally { setBusy(false); }
  };

  const submitAssist = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const player = sortedPlayers.find(p => p.id === assistId);
      const res = await api.post(`/scores/soccer/${gameId}/event`, {
        event_type: 'assist', team: ourTeamSide,
        player_name: player?.name ?? '', player_id: assistId || null,
        minute: parseInt(minute) || currentMinute,
      });
      onUpdate(res.data);  // immediate update
    } catch {}
    finally { setBusy(false); resetForm(); }
  };

  const submitOppGoal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post(`/scores/soccer/${gameId}/event`, {
        event_type: 'goal', team: oppTeamSide,
        player_name: oppNumber ? `#${oppNumber}` : '', player_id: null,
        minute: parseInt(minute) || currentMinute,
      });
      onUpdate(res.data);  // immediate update
      flash(oppTeamSide);
      resetForm();
    } catch (err: any) { alert(err?.response?.data?.error || 'Failed to log goal.'); }
    finally { setBusy(false); }
  };

  const deleteEvent = async (eventId: string) => {
    try {
      const res = await api.delete(`/scores/soccer/${gameId}/event/${eventId}`);
      onUpdate(res.data);  // immediate update
    } catch {}
  };

  const recentEvents = [...(score.events || [])].reverse().slice(0, 5);

  // ── Shared styles ──────────────────────────────────────────────────────────
  const bigBtn = (color: string, bg: string): React.CSSProperties => ({
    flex: 1, padding: '18px 10px', borderRadius: '12px', cursor: 'pointer',
    border: `2px solid ${color}`, background: bg,
    color, fontWeight: 800, fontSize: '15px', touchAction: 'manipulation',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
    transition: 'opacity 0.15s',
  });

  const playerSelect = (value: string, onChange: (v: string) => void, exclude?: string) => (
    <select className="form-input" value={value} onChange={e => onChange(e.target.value)}
      style={{ flex: 1, fontSize: '15px' }}>
      <option value="">— Player (optional) —</option>
      {sortedPlayers.filter(p => p.id !== exclude).map(p => (
        <option key={p.id} value={p.id}>{p.number != null ? `#${p.number} ` : ''}{p.name}</option>
      ))}
    </select>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: 'var(--surface-muted)', border: '1px solid var(--hairline)', borderRadius: '18px', overflow: 'hidden', marginTop: '12px' }}>

      {/* ── Scoreboard ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        <div style={{
          flex: 1, padding: '16px 12px', textAlign: 'center',
          background: flashTeam === 'away' ? 'rgba(239,68,68,0.2)' : 'var(--surface-muted)',
          transition: 'background 0.5s', borderRight: '1px solid var(--hairline)',
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{awayTeam}</div>
          <div style={{ fontSize: '48px', fontWeight: 900, color: 'var(--ink-1)', lineHeight: 1 }}>{score.away_score}</div>
        </div>
        <div style={{ padding: '12px 8px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: '80px' }}>
          <div style={{ fontSize: '12px', fontWeight: 800, color: '#F97316', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {PERIOD_LABELS[st] ?? st}
          </div>
          {isTimerActive && (
            <div style={{ fontSize: '22px', fontWeight: 900, color: 'var(--ink-1)', lineHeight: 1.2, marginTop: '2px' }}>
              {Math.floor(timerMs / 60000)}'
            </div>
          )}
          {isTimerActive && !isRunning && (
            <div style={{ fontSize: '10px', color: '#EAB308', fontWeight: 700, marginTop: '2px' }}>PAUSED</div>
          )}
        </div>
        <div style={{
          flex: 1, padding: '16px 12px', textAlign: 'center',
          background: flashTeam === 'home' ? 'rgba(34,197,94,0.2)' : 'var(--surface-muted)',
          transition: 'background 0.5s', borderLeft: '1px solid var(--hairline)',
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{homeTeam}</div>
          <div style={{ fontSize: '48px', fontWeight: 900, color: 'var(--ink-1)', lineHeight: 1 }}>{score.home_score}</div>
        </div>
      </div>

      <div style={{ padding: '14px 14px 0' }}>

        {/* ── Smart Timer Row ──────────────────────────────────────────────── */}
        {primaryBtn && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button onClick={primaryBtn.fn} disabled={timerBusy}
              style={{
                flex: 1, padding: '14px', borderRadius: '12px', border: 'none',
                background: primaryBtn.color, color: '#fff',
                fontWeight: 800, fontSize: '15px', cursor: 'pointer',
                touchAction: 'manipulation', opacity: timerBusy ? 0.6 : 1,
                transition: 'opacity 0.15s',
              }}>
              {primaryBtn.label}
            </button>
            {/* Pause / Resume during active half */}
            {isTimerActive && (
              <button onClick={() => timerAction(isRunning ? 'pause' : 'start')} disabled={timerBusy}
                style={{
                  padding: '14px 16px', borderRadius: '12px',
                  border: '1px solid var(--hairline)', background: 'var(--surface-muted)',
                  color: 'var(--ink-3)', fontWeight: 700, fontSize: '13px',
                  cursor: 'pointer', touchAction: 'manipulation', whiteSpace: 'nowrap',
                }}>
                {isRunning ? '⏸ Pause' : '▶ Resume'}
              </button>
            )}
          </div>
        )}

        {/* After full time: OT / Penalties / Complete options */}
        {['full_time', 'extra_time_full', 'penalties'].includes(st) && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {st === 'extra_time_full' && (
              <button onClick={() => timerAction('pause', 'penalties')} disabled={timerBusy}
                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #A78BFA', background: 'rgba(167,139,250,0.1)', color: '#A78BFA', fontWeight: 700, fontSize: '13px', cursor: 'pointer', touchAction: 'manipulation' }}>
                🥅 Penalties
              </button>
            )}
            <button onClick={onCompleteGame} disabled={timerBusy}
              style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #22C55E', background: 'rgba(34,197,94,0.1)', color: '#22C55E', fontWeight: 800, fontSize: '14px', cursor: 'pointer', touchAction: 'manipulation' }}>
              ✓ Complete Game
            </button>
          </div>
        )}

        {/* ── Our Team Selector ────────────────────────────────────────────── */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>
            Our Team
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {(['home', 'away'] as const).map(side => {
              const name = side === 'home' ? homeTeam : awayTeam;
              const active = ourTeamSide === side;
              return (
                <button key={side} onClick={() => onSetOurSide(side)}
                  style={{
                    flex: 1, padding: '8px 10px', borderRadius: '8px', cursor: 'pointer',
                    border: `1.5px solid ${active ? '#F97316' : 'var(--hairline)'}`,
                    background: active ? 'rgba(249,115,22,0.12)' : 'var(--surface-muted)',
                    color: active ? '#F97316' : 'var(--ink-4)',
                    fontWeight: active ? 800 : 500, fontSize: '12px',
                    transition: 'all 0.15s', textAlign: 'center',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                  <span style={{ fontSize: '9px', opacity: 0.7, display: 'block' }}>{side.toUpperCase()}</span>
                  {name}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Goal Buttons (always visible) ────────────────────────────────── */}
        {phase === 'idle' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <button onClick={openOurGoal} style={bigBtn('#22C55E', 'rgba(34,197,94,0.08)')}>
                <span style={{ fontSize: '26px' }}>⚽</span>
                <span>{ourSide ? 'Our Goal' : 'Home Goal'}</span>
                <span style={{ fontSize: '11px', fontWeight: 600, opacity: 0.7, textAlign: 'center', lineHeight: 1.2 }}>{ourTeamName}</span>
              </button>
              <button onClick={openOppGoal} style={bigBtn('#EF4444', 'rgba(239,68,68,0.06)')}>
                <span style={{ fontSize: '26px' }}>⚽</span>
                <span>{ourSide ? 'Opp Goal' : 'Away Goal'}</span>
                <span style={{ fontSize: '11px', fontWeight: 600, opacity: 0.7, textAlign: 'center', lineHeight: 1.2 }}>{oppTeamName}</span>
              </button>
            </div>

            {/* Recent Events */}
            {recentEvents.length > 0 && (
              <div style={{ marginBottom: '4px' }}>
                <div style={{ fontSize: '10px', color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
                  Events
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {recentEvents.map(ev => {
                    const isHome = ev.team === 'home';
                    const evColor = ev.event_type === 'goal' ? (isHome ? '#22C55E' : '#EF4444')
                      : ev.event_type === 'assist' ? '#F97316'
                      : ev.event_type === 'yellow_card' ? '#EAB308' : 'var(--ink-3)';
                    const evIcon = ev.event_type === 'goal' ? '⚽' : ev.event_type === 'assist' ? '🎯'
                      : ev.event_type === 'yellow_card' ? '🟨' : ev.event_type === 'red_card' ? '🟥' : '🔄';
                    return (
                      <div key={ev.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '6px 10px', borderRadius: '8px',
                        background: 'var(--surface-muted)', border: `1px solid ${evColor}18`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '14px' }}>{evIcon}</span>
                          <span style={{ fontSize: '12px', fontWeight: 800, color: evColor }}>{ev.minute}'</span>
                          <span style={{ fontSize: '12px', color: 'var(--ink-3)' }}>{isHome ? homeTeam : awayTeam}</span>
                          {ev.player_name && <span style={{ fontSize: '12px', color: 'var(--ink-1)' }}>— {ev.player_name}</span>}
                        </div>
                        <button onClick={() => deleteEvent(ev.id)} title="Undo"
                          style={{ background: 'none', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: '16px', padding: '2px 6px', lineHeight: 1 }}>
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Our Goal Form ─────────────────────────────────────────────────── */}
        {phase === 'goal-our' && (
          <div style={{ padding: '4px 0 8px' }}>
            <div style={{ fontSize: '13px', fontWeight: 800, color: '#22C55E', marginBottom: '12px' }}>
              ⚽ Goal — {ourTeamName}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
              {teamPlayers.length > 0 ? playerSelect(scorerId, setScorerId) : (
                <div style={{ flex: 1, fontSize: '13px', color: 'var(--ink-3)', fontStyle: 'italic' }}>No roster — goal recorded without scorer.</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                <span style={{ fontSize: '10px', color: 'var(--ink-4)', fontWeight: 700 }}>MIN</span>
                <input className="form-input" type="number" value={minute}
                  onChange={e => setMinute(e.target.value)} min={0} max={120}
                  style={{ width: '54px', fontSize: '18px', fontWeight: 800, textAlign: 'center', padding: '6px 4px' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={submitOurGoal} disabled={busy}
                style={{ flex: 1, padding: '14px', borderRadius: '10px', border: 'none', background: '#22C55E', color: '#fff', fontWeight: 800, fontSize: '15px', cursor: 'pointer', touchAction: 'manipulation' }}>
                {busy ? 'Saving…' : '✓ Confirm Goal'}
              </button>
              <button onClick={resetForm}
                style={{ padding: '14px 18px', borderRadius: '10px', border: '1px solid var(--hairline)', background: 'transparent', color: 'var(--ink-3)', fontWeight: 700, fontSize: '14px', cursor: 'pointer', touchAction: 'manipulation' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Goal Logged → Assist Prompt ───────────────────────────────────── */}
        {phase === 'ask-assist' && (
          <div style={{ padding: '4px 0 8px' }}>
            <div style={{ fontSize: '14px', fontWeight: 800, color: '#22C55E', marginBottom: '4px' }}>⚽ Goal logged!</div>
            <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginBottom: '12px' }}>Add an assist?</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setPhase('assist-our')}
                style={{ flex: 1, padding: '13px', borderRadius: '10px', border: '2px solid #F97316', background: 'rgba(249,115,22,0.1)', color: '#F97316', fontWeight: 800, fontSize: '14px', cursor: 'pointer', touchAction: 'manipulation' }}>
                🎯 Add Assist
              </button>
              <button onClick={resetForm}
                style={{ padding: '13px 18px', borderRadius: '10px', border: '1px solid var(--hairline)', background: 'transparent', color: 'var(--ink-3)', fontWeight: 700, fontSize: '14px', cursor: 'pointer', touchAction: 'manipulation' }}>
                Skip
              </button>
            </div>
          </div>
        )}

        {/* ── Assist Form ───────────────────────────────────────────────────── */}
        {phase === 'assist-our' && (
          <div style={{ padding: '4px 0 8px' }}>
            <div style={{ fontSize: '13px', fontWeight: 800, color: '#F97316', marginBottom: '12px' }}>
              🎯 Assist — {ourTeamName}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
              {teamPlayers.length > 0 ? playerSelect(assistId, setAssistId, scorerId) : (
                <div style={{ flex: 1, fontSize: '13px', color: 'var(--ink-3)', fontStyle: 'italic' }}>No roster.</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                <span style={{ fontSize: '10px', color: 'var(--ink-4)', fontWeight: 700 }}>MIN</span>
                <input className="form-input" type="number" value={minute}
                  onChange={e => setMinute(e.target.value)} min={0} max={120}
                  style={{ width: '54px', fontSize: '18px', fontWeight: 800, textAlign: 'center', padding: '6px 4px' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={submitAssist} disabled={busy}
                style={{ flex: 1, padding: '14px', borderRadius: '10px', border: 'none', background: '#F97316', color: '#fff', fontWeight: 800, fontSize: '15px', cursor: 'pointer', touchAction: 'manipulation' }}>
                {busy ? 'Saving…' : '✓ Log Assist'}
              </button>
              <button onClick={resetForm}
                style={{ padding: '14px 18px', borderRadius: '10px', border: '1px solid var(--hairline)', background: 'transparent', color: 'var(--ink-3)', fontWeight: 700, fontSize: '14px', cursor: 'pointer', touchAction: 'manipulation' }}>
                Skip
              </button>
            </div>
          </div>
        )}

        {/* ── Opponent Goal Form ────────────────────────────────────────────── */}
        {phase === 'goal-opp' && (
          <div style={{ padding: '4px 0 8px' }}>
            <div style={{ fontSize: '13px', fontWeight: 800, color: '#EF4444', marginBottom: '12px' }}>
              ⚽ Opponent Goal — {oppTeamName}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
              <input className="form-input" type="number" placeholder="Jersey # (optional)"
                value={oppNumber} onChange={e => setOppNumber(e.target.value)} min={0} max={99}
                style={{ flex: 1, fontSize: '15px', fontWeight: 700 }} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                <span style={{ fontSize: '10px', color: 'var(--ink-4)', fontWeight: 700 }}>MIN</span>
                <input className="form-input" type="number" value={minute}
                  onChange={e => setMinute(e.target.value)} min={0} max={120}
                  style={{ width: '54px', fontSize: '18px', fontWeight: 800, textAlign: 'center', padding: '6px 4px' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={submitOppGoal} disabled={busy}
                style={{ flex: 1, padding: '14px', borderRadius: '10px', border: 'none', background: '#EF4444', color: '#fff', fontWeight: 800, fontSize: '15px', cursor: 'pointer', touchAction: 'manipulation' }}>
                {busy ? 'Saving…' : '✓ Confirm Goal'}
              </button>
              <button onClick={resetForm}
                style={{ padding: '14px 18px', borderRadius: '10px', border: '1px solid var(--hairline)', background: 'transparent', color: 'var(--ink-3)', fontWeight: 700, fontSize: '14px', cursor: 'pointer', touchAction: 'manipulation' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

      </div>
      {/* bottom padding */}
      <div style={{ height: '14px' }} />
    </div>
  );
};

// ── Live overlay admin control panel ─────────────────────────────────────────
const OverlayControlPanel: React.FC<{
  gameId: string;
  overlayOn: boolean; tickerOn: boolean;
  overlaySponsorId: string | null; overlaySponsor: any | null;
  customTicker: string[]; cornerLogoUrl: string | null;
  busy: boolean;
  onToggleOverlay:        (val: boolean)              => Promise<void>;
  onToggleTicker:         (val: boolean)              => Promise<void>;
  onChangeSponsor:        (id: string | null, spn: any | null) => Promise<void>;
  onChangeCustomTicker:   (items: string[])           => Promise<void>;
  onChangeCornerLogo:     (url: string | null)        => Promise<void>;
}> = ({
  overlayOn, tickerOn, overlaySponsorId, overlaySponsor,
  customTicker, cornerLogoUrl, busy,
  onToggleOverlay, onToggleTicker, onChangeSponsor,
  onChangeCustomTicker, onChangeCornerLogo,
}) => {
  const [sponsors, setSponsors] = useState<any[]>([]);
  const [teams,    setTeams]    = useState<any[]>([]);
  const [open, setOpen]         = useState(false);
  const [newMsg, setNewMsg]     = useState('');

  useEffect(() => {
    api.get('/sponsors').then(r => setSponsors(r.data || [])).catch(() => {});
    api.get('/teams').then(r => setTeams(r.data || [])).catch(() => {});
  }, []);

  const addMsg = async () => {
    const trimmed = newMsg.trim();
    if (!trimmed) return;
    const updated = [...customTicker, trimmed];
    await onChangeCustomTicker(updated);
    setNewMsg('');
  };

  const removeMsg = async (idx: number) => {
    await onChangeCustomTicker(customTicker.filter((_, i) => i !== idx));
  };

  const toggle: React.CSSProperties = {
    position: 'relative', width: 44, height: 24, borderRadius: 12,
    cursor: 'pointer', border: 'none', transition: 'background 0.2s',
    flexShrink: 0, touchAction: 'manipulation',
  };
  const knob: React.CSSProperties = {
    position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%',
    background: '#fff', transition: 'left 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  };
  const sectionLabel = (text: string, sub?: string) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>
      {text}{sub && <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--ink-3)' }}> — {sub}</span>}
    </div>
  );

  const row = (label: string, sublabel: string, active: boolean, onChange: () => void) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--hairline)' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-1)' }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 1 }}>{sublabel}</div>
      </div>
      <button
        onClick={onChange}
        disabled={busy}
        style={{ ...toggle, background: active ? '#22C55E' : 'rgba(0,0,0,0.15)' }}
        aria-checked={active}
        role="switch"
      >
        <span style={{ ...knob, left: active ? 23 : 3 }} />
      </button>
    </div>
  );

  const activeCount = [overlayOn, tickerOn, customTicker.length > 0, !!cornerLogoUrl].filter(Boolean).length;

  return (
    <div style={{
      background: 'var(--surface-muted)', border: '1px solid var(--hairline)',
      borderRadius: 16, overflow: 'hidden', marginTop: 12,
    }}>
      {/* Header / toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 16 }}>📺</span>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 800, color: 'var(--ink-1)' }}>Live Overlay Controls</span>
        <span style={{ fontSize: 11, color: activeCount > 0 ? '#22C55E' : 'var(--ink-4)', marginRight: 4, fontWeight: 600 }}>
          {activeCount > 0 ? `● ${activeCount} active` : 'Off'}
        </span>
        <span style={{ fontSize: 13, color: 'var(--ink-4)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px' }}>

          {/* ── Toggle rows ── */}
          {row(
            'Scoreboard Bug',
            'ESPN-style top-left score bug with team logos, score, and clock',
            overlayOn,
            () => onToggleOverlay(!overlayOn),
          )}
          {row(
            'Stats Ticker',
            'Scrolling ticker at the bottom of the video (goals, assists, custom messages)',
            tickerOn,
            () => onToggleTicker(!tickerOn),
          )}

          {/* ── Overlay sponsor picker ── */}
          <div style={{ paddingTop: 12 }}>
            {sectionLabel('Score Bug Sponsor', 'logo shown as "Presented by" in the score bug')}
            <select
              className="form-input"
              value={overlaySponsorId ?? ''}
              onChange={async e => {
                const id  = e.target.value || null;
                const spn = id ? sponsors.find(s => s.id === id) ?? null : null;
                await onChangeSponsor(id, spn);
              }}
              disabled={busy}
              style={{ fontSize: 13 }}
            >
              <option value="">— None —</option>
              {sponsors.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {overlaySponsor && (
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                {overlaySponsor.logo_url && (
                  <img
                    src={mediaUrl(overlaySponsor.logo_url)}
                    alt={overlaySponsor.name}
                    style={{ height: 28, maxWidth: 72, objectFit: 'contain', borderRadius: 4, background: 'rgba(0,0,0,0.1)', padding: 3 }}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  <strong>{overlaySponsor.name}</strong>
                  {overlaySponsor.tagline && ` — ${overlaySponsor.tagline}`}
                </span>
              </div>
            )}
          </div>

          {/* ── Custom ticker messages ── */}
          <div style={{ paddingTop: 14, borderTop: '1px solid var(--hairline)', marginTop: 14 }}>
            {sectionLabel('Custom Ticker Messages', 'shown first in the scrolling ticker')}
            {customTicker.map((msg, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                <div style={{
                  flex: 1, fontSize: 12, color: 'var(--ink-2)', padding: '4px 8px',
                  background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--hairline)',
                  fontStyle: 'italic',
                }}>
                  📢 {msg}
                </div>
                <button
                  onClick={() => removeMsg(i)}
                  disabled={busy}
                  style={{ background: 'none', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0, lineHeight: 1 }}
                >
                  ✕
                </button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6, marginTop: customTicker.length ? 6 : 0 }}>
              <input
                type="text"
                placeholder="e.g. Game delayed 10 minutes"
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addMsg(); }}
                style={{
                  flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 6,
                  border: '1px solid var(--hairline)', background: 'var(--surface)',
                  color: 'var(--ink-1)', outline: 'none',
                }}
              />
              <button
                onClick={addMsg}
                disabled={!newMsg.trim() || busy}
                style={{
                  padding: '6px 12px', borderRadius: 6, background: '#F97316', color: '#fff',
                  border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                  flexShrink: 0, opacity: !newMsg.trim() ? 0.5 : 1,
                  touchAction: 'manipulation',
                }}
              >
                Add
              </button>
            </div>
          </div>

          {/* ── Top-right corner logo / ad ── */}
          <div style={{ paddingTop: 14, borderTop: '1px solid var(--hairline)', marginTop: 14 }}>
            {sectionLabel('Top-Right Corner Logo / Ad', 'team badge or sponsor ad shown top-right of the video')}
            <select
              className="form-input"
              value={cornerLogoUrl ?? ''}
              onChange={async e => {
                await onChangeCornerLogo(e.target.value || null);
              }}
              disabled={busy}
              style={{ fontSize: 13 }}
            >
              <option value="">— None —</option>
              {teams.filter(t => t.logo_url).length > 0 && (
                <optgroup label="Team Logos">
                  {teams.filter(t => t.logo_url).map(t => (
                    <option key={`team-${t.id}`} value={t.logo_url}>{t.name}</option>
                  ))}
                </optgroup>
              )}
              {sponsors.filter(s => s.logo_url).length > 0 && (
                <optgroup label="Sponsor Logos">
                  {sponsors.filter(s => s.logo_url).map(s => (
                    <option key={`spn-${s.id}`} value={s.logo_url}>{s.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
            {cornerLogoUrl && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 72, height: 48, borderRadius: 6, background: 'rgba(0,0,0,0.75)',
                  border: '1px solid rgba(255,255,255,0.12)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: 6, flexShrink: 0,
                }}>
                  <img
                    src={mediaUrl(cornerLogoUrl)}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Preview (dark background matches stream)</span>
              </div>
            )}
          </div>

          {busy && (
            <div style={{ marginTop: 12, fontSize: 11, color: 'var(--ink-4)', textAlign: 'center' }}>
              Updating…
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Complete-game confirmation modal ─────────────────────────────────────────
type PlayerRec = { played: boolean; goals: number; assists: number };

const CompleteGameModal = ({ gameId, score, teamPlayers, ourSide, homeTeam, awayTeam, onClose, onCompleted }: {
  gameId: string; score: SoccerScore; teamPlayers: TeamPlayer[];
  ourSide: 'home' | 'away' | null; homeTeam: string; awayTeam: string;
  onClose: () => void; onCompleted: () => void;
}) => {
  // Aggregate goals/assists from live events that had player_id attached
  const eventStats: Record<string, { goals: number; assists: number }> = {};
  for (const ev of (score.events || [])) {
    if (!ev.player_id) continue;
    if (!eventStats[ev.player_id]) eventStats[ev.player_id] = { goals: 0, assists: 0 };
    if (ev.event_type === 'goal')   eventStats[ev.player_id].goals++;
    if (ev.event_type === 'assist') eventStats[ev.player_id].assists++;
  }

  const sortedPlayers = [...teamPlayers].sort((a, b) => (a.number ?? 99) - (b.number ?? 99));

  // Pre-fill: mark played=true for anyone who scored/assisted in live events
  const [records, setRecords] = useState<Record<string, PlayerRec>>(() => {
    const init: Record<string, PlayerRec> = {};
    for (const p of sortedPlayers) {
      const ev = eventStats[p.id];
      init[p.id] = { played: !!ev, goals: ev?.goals ?? 0, assists: ev?.assists ?? 0 };
    }
    return init;
  });

  // Score is pre-filled from the live scoreboard
  const [homeScore, setHomeScore] = useState(String(score.home_score ?? 0));
  const [awayScore, setAwayScore] = useState(String(score.away_score ?? 0));
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState('');

  // Live result badge — updates as the user edits scores
  const ourFinalScore = ourSide === 'home' ? parseInt(homeScore) || 0 : parseInt(awayScore) || 0;
  const oppFinalScore = ourSide === 'home' ? parseInt(awayScore) || 0 : parseInt(homeScore) || 0;
  const result: 'win' | 'loss' | 'tie' | null = ourSide
    ? ourFinalScore > oppFinalScore ? 'win'
    : ourFinalScore < oppFinalScore ? 'loss'
    : 'tie'
    : null;
  const resultColor = result === 'win' ? '#22C55E' : result === 'loss' ? '#EF4444' : '#F97316';

  const togglePlayed = (id: string) =>
    setRecords(r => ({ ...r, [id]: { ...r[id], played: !r[id]?.played } }));

  const updateStat = (id: string, field: 'goals' | 'assists', val: string) =>
    setRecords(r => ({ ...r, [id]: { ...r[id], [field]: Math.max(0, parseInt(val) || 0) } }));

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await api.post(`/games/${gameId}/complete`, {
        home_final_score: parseInt(homeScore) || 0,
        away_final_score: parseInt(awayScore) || 0,
        player_records: sortedPlayers.map(p => ({
          player_id: p.id,
          played:    records[p.id]?.played  ?? false,
          goals:     records[p.id]?.goals   ?? 0,
          assists:   records[p.id]?.assists  ?? 0,
        })),
      });
      onCompleted();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to complete game. Please try again.');
    } finally { setBusy(false); }
  };

  const inputNum: React.CSSProperties = {
    textAlign: 'center', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)',
    borderRadius: '6px', padding: '4px 6px', color: 'var(--ink-1)',
    fontSize: '14px', fontWeight: 700, width: '56px',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      zIndex: 2000, overflowY: 'auto', padding: '24px 16px 48px',
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px',
        width: '100%', maxWidth: '560px', padding: '24px',
        boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{ fontSize: '16px', fontWeight: 800, color: '#22C55E', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          ✓ Complete Game
          <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--ink-4)', fontWeight: 500 }}>Review &amp; confirm before saving</span>
        </div>

        {/* ── Final Score ─────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Confirm Final Score
            </div>
            {result && (
              <div style={{
                padding: '4px 14px', borderRadius: '20px', fontWeight: 900,
                fontSize: '13px', letterSpacing: '1px',
                background: `${resultColor}22`, border: `1px solid ${resultColor}55`,
                color: resultColor,
              }}>
                {result === 'win' ? 'WIN' : result === 'loss' ? 'LOSS' : 'TIE'}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Away team */}
            <div style={{
              flex: 1, textAlign: 'center', padding: '12px 8px', borderRadius: '10px',
              border: `1px solid ${ourSide === 'away' ? resultColor + '66' : 'var(--hairline)'}`,
              background: ourSide === 'away' ? `${resultColor}0D` : 'transparent',
            }}>
              {ourSide === 'away' && (
                <div style={{ fontSize: '9px', fontWeight: 800, color: resultColor, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>
                  OUR TEAM
                </div>
              )}
              <div style={{ fontSize: '11px', color: ourSide === 'away' ? 'var(--ink-1)' : 'var(--ink-3)', marginBottom: '6px', fontWeight: 600 }}>
                AWAY · {awayTeam}
              </div>
              <input type="number" min={0} value={awayScore} onChange={e => setAwayScore(e.target.value)}
                className="form-input"
                style={{ textAlign: 'center', fontSize: '32px', fontWeight: 900, padding: '10px 8px' }} />
            </div>
            <div style={{ fontSize: '24px', color: 'var(--hairline)', fontWeight: 700, flexShrink: 0 }}>–</div>
            {/* Home team */}
            <div style={{
              flex: 1, textAlign: 'center', padding: '12px 8px', borderRadius: '10px',
              border: `1px solid ${ourSide === 'home' ? resultColor + '66' : 'var(--hairline)'}`,
              background: ourSide === 'home' ? `${resultColor}0D` : 'transparent',
            }}>
              {ourSide === 'home' && (
                <div style={{ fontSize: '9px', fontWeight: 800, color: resultColor, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>
                  OUR TEAM
                </div>
              )}
              <div style={{ fontSize: '11px', color: ourSide === 'home' ? 'var(--ink-1)' : 'var(--ink-3)', marginBottom: '6px', fontWeight: 600 }}>
                HOME · {homeTeam}
              </div>
              <input type="number" min={0} value={homeScore} onChange={e => setHomeScore(e.target.value)}
                className="form-input"
                style={{ textAlign: 'center', fontSize: '32px', fontWeight: 900, padding: '10px 8px' }} />
            </div>
          </div>
          {/* Warning if ourSide unknown */}
          {!ourSide && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#EAB308', background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.25)', borderRadius: '8px', padding: '8px 12px' }}>
              Our team side is not set — verify which score belongs to which team before saving.
            </div>
          )}
        </div>

        {/* ── Player Participation ─────────────────────────────────────────────── */}
        {sortedPlayers.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
              Who Played? <span style={{ color: 'var(--ink-3)', fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: '11px' }}>— tap to mark as Game Played (GP)</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
              {sortedPlayers.map(p => {
                const played = records[p.id]?.played ?? false;
                return (
                  <button key={p.id} onClick={() => togglePlayed(p.id)} style={{
                    padding: '6px 12px', borderRadius: '8px', cursor: 'pointer',
                    border: `1px solid ${played ? '#22C55E' : 'var(--hairline)'}`,
                    background: played ? 'rgba(34,197,94,0.12)' : 'var(--surface-muted)',
                    color: played ? '#22C55E' : 'var(--ink-4)',
                    fontSize: '13px', fontWeight: 600, touchAction: 'manipulation',
                    transition: 'all 0.15s',
                  }}>
                    {p.number != null ? `#${p.number} ` : ''}{p.name}
                  </button>
                );
              })}
            </div>

            {/* ── Per-player stats (goals / assists) ─────────────────────────── */}
            {sortedPlayers.some(p => records[p.id]?.played) && (
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  Player Stats (edit to correct)
                </div>
                <div style={{ border: '1px solid var(--hairline)', borderRadius: '8px', overflow: 'hidden' }}>
                  {/* Header row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 72px 72px', padding: '8px 12px', background: 'var(--surface-sunken)', fontSize: '11px', color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    <div>Player</div>
                    <div style={{ textAlign: 'center' }}>Goals</div>
                    <div style={{ textAlign: 'center' }}>Assists</div>
                  </div>
                  {sortedPlayers.filter(p => records[p.id]?.played).map((p, i) => (
                    <div key={p.id} style={{
                      display: 'grid', gridTemplateColumns: '1fr 72px 72px',
                      padding: '8px 12px', alignItems: 'center',
                      background: i % 2 === 0 ? 'transparent' : 'var(--surface-muted)',
                      borderTop: '1px solid var(--hairline)',
                    }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink-1)' }}>
                        {p.number != null && <span style={{ color: 'var(--ink-3)', marginRight: '4px' }}>#{p.number}</span>}
                        {p.name}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <input type="number" min={0} value={records[p.id]?.goals ?? 0}
                          onChange={e => updateStat(p.id, 'goals', e.target.value)}
                          style={inputNum} />
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <input type="number" min={0} value={records[p.id]?.assists ?? 0}
                          onChange={e => updateStat(p.id, 'assists', e.target.value)}
                          style={inputNum} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
            fontSize: '13px', color: '#F87171',
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={submit} disabled={busy} className="btn btn-primary btn-lg" style={{ flex: 1 }}>
            {busy ? 'Saving...' : '✓ Save & Complete Game'}
          </button>
          <button onClick={onClose} disabled={busy} className="btn btn-ghost btn-lg">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Baseball scoring controls ─────────────────────────────────────────────────

// ── Basketball scoring controls ───────────────────────────────────────────────
const BBALL_STATUSES: { value: BasketballStatus; label: string }[] = [
  { value: 'not_started',    label: 'Pre-Game' },
  { value: 'first_quarter',  label: '1st Quarter' },
  { value: 'second_quarter', label: '2nd Quarter' },
  { value: 'halftime',       label: 'Half Time' },
  { value: 'third_quarter',  label: '3rd Quarter' },
  { value: 'fourth_quarter', label: '4th Quarter' },
  { value: 'overtime',       label: 'Overtime' },
  { value: 'final',          label: 'Final' },
];

const BasketballScoringControls: React.FC<{
  gameId: string;
  score: BasketballScore;
  homeTeam: string;
  awayTeam: string;
  onScoreUpdate: (s: BasketballScore) => void;
}> = ({ gameId, score, homeTeam, awayTeam, onScoreUpdate }) => {
  const [busy, setBusy] = useState(false);

  const update = async (patch: Partial<BasketballScore>) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.put(`/scores/basketball/${gameId}`, patch);
      onScoreUpdate(r.data);
    } finally { setBusy(false); }
  };

  const bump = (team: 'home' | 'away', delta: number) => {
    const field = team === 'home' ? 'home_score' : 'away_score';
    const cur = team === 'home' ? score.home_score : score.away_score;
    update({ [field]: Math.max(0, cur + delta) });
  };

  const btnStyle = (color = '#F97316'): React.CSSProperties => ({
    width: 44, height: 44, borderRadius: 8, border: `1px solid ${color}33`,
    background: `${color}18`, color, fontSize: 22, fontWeight: 900, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'manipulation',
    opacity: busy ? 0.5 : 1,
  });

  const row: React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 };
  const teamLabel: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.6px', textAlign: 'center', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 14, padding: 20, marginTop: 16 }}>
      <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 13, marginBottom: 16, color: 'var(--ink-1)' }}>
        🏀 Basketball Scoring
      </div>

      {/* Scoreboard */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 12, marginBottom: 20 }}>
        {/* Away */}
        <div style={row}>
          <div style={teamLabel}>{awayTeam}</div>
          <div style={{ fontSize: 52, fontWeight: 900, color: score.away_score >= score.home_score && score.status === 'final' ? '#22C55E' : 'var(--ink-1)', lineHeight: 1 }}>
            {score.away_score}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={btnStyle()} onClick={() => bump('away', 1)}>+</button>
            <button style={btnStyle('#EF4444')} onClick={() => bump('away', -1)}>−</button>
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
            {[1, 2, 3].map(n => (
              <button key={n} onClick={() => bump('away', n)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', color: '#F97316', cursor: 'pointer', fontWeight: 700 }}>+{n}</button>
            ))}
          </div>
        </div>

        {/* Center — quarter & status */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase' }}>Period</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => update({ quarter: Math.max(1, (score.quarter || 1) - 1) })} style={{ ...btnStyle('var(--ink-3)'), width: 32, height: 32, fontSize: 16 }}>‹</button>
            <div style={{ width: 36, textAlign: 'center', fontSize: 22, fontWeight: 900, color: 'var(--ink-1)' }}>
              {score.quarter > 4 ? 'OT' : `Q${score.quarter}`}
            </div>
            <button onClick={() => update({ quarter: Math.min(5, (score.quarter || 1) + 1) })} style={{ ...btnStyle('var(--ink-3)'), width: 32, height: 32, fontSize: 16 }}>›</button>
          </div>
          <select
            value={score.status || 'not_started'}
            onChange={e => update({ status: e.target.value as BasketballStatus })}
            style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', color: 'var(--ink-1)', cursor: 'pointer' }}
          >
            {BBALL_STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* Home */}
        <div style={row}>
          <div style={teamLabel}>{homeTeam}</div>
          <div style={{ fontSize: 52, fontWeight: 900, color: score.home_score >= score.away_score && score.status === 'final' ? '#22C55E' : 'var(--ink-1)', lineHeight: 1 }}>
            {score.home_score}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={btnStyle()} onClick={() => bump('home', 1)}>+</button>
            <button style={btnStyle('#EF4444')} onClick={() => bump('home', -1)}>−</button>
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
            {[1, 2, 3].map(n => (
              <button key={n} onClick={() => bump('home', n)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', color: '#F97316', cursor: 'pointer', fontWeight: 700 }}>+{n}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Highlight Upload Modal ─────────────────────────────────────────────────────
const HighlightUploadModal: React.FC<{
  gameId: string;
  onClose: () => void;
  onUploaded: () => void;
}> = ({ gameId, onClose, onUploaded }) => {
  const [file,     setFile]     = React.useState<File | null>(null);
  const [title,    setTitle]    = React.useState('');
  const [progress, setProgress] = React.useState(0);
  const [loading,  setLoading]  = React.useState(false);
  const [error,    setError]    = React.useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Please select a video file'); return; }
    setLoading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('video', file);
      fd.append('game_id', gameId);
      if (title.trim()) fd.append('title', title.trim());
      await api.post('/videos/upload-game-highlight', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e: any) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      onUploaded();
      onClose();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Upload failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h2>🎬 Upload Highlight Reel</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title (optional)</label>
            <input className="form-control" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Game Highlights, Best Plays…" />
          </div>
          <div className="form-group">
            <label>Video File</label>
            <input type="file" accept="video/*" className="form-control" style={{ padding: '8px' }}
              onChange={e => setFile(e.target.files?.[0] || null)} />
            {file && (
              <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginTop: '6px' }}>
                {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
              </div>
            )}
          </div>

          {loading && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--ink-3)', marginBottom: '6px' }}>
                <span>Uploading…</span><span>{progress}%</span>
              </div>
              <div style={{ height: 6, background: 'var(--hairline)', borderRadius: 4 }}>
                <div style={{ height: '100%', width: `${progress}%`, background: '#F97316', borderRadius: 4, transition: 'width 0.2s' }} />
              </div>
            </div>
          )}

          {error && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !file}>
              {loading ? 'Uploading…' : '⬆️ Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BaseballScoringControls: React.FC<{
  gameId: string;
  score: BaseballScore;
  homeTeam: string;
  awayTeam: string;
  onScoreUpdate: (s: BaseballScore) => void;
}> = ({ gameId, score, homeTeam, awayTeam, onScoreUpdate }) => {
  const maxInn = Math.max(7, score.current_inning);
  const [innVals, setInnVals] = useState<Record<number, { home: number; away: number }>>(() => {
    const m: Record<number, { home: number; away: number }> = {};
    for (let i = 1; i <= maxInn; i++) {
      const f = score.inning_scores?.find((s: any) => s.inning === i);
      m[i] = { home: f?.home ?? 0, away: f?.away ?? 0 };
    }
    return m;
  });
  const [innings, setInnings] = useState(maxInn);
  const [busy, setBusy] = useState(false);

  // Keep innVals in sync when innings count grows
  useEffect(() => {
    setInnVals(prev => {
      const m = { ...prev };
      for (let i = 1; i <= innings; i++) {
        if (!m[i]) m[i] = { home: 0, away: 0 };
      }
      return m;
    });
  }, [innings]);

  const save = async (extra?: Partial<BaseballScore>) => {
    setBusy(true);
    try {
      const scores = Object.entries(innVals).map(([inn, v]) => ({ inning: Number(inn), home: v.home, away: v.away }));
      const homeTotal = scores.reduce((s, v) => s + v.home, 0);
      const awayTotal = scores.reduce((s, v) => s + v.away, 0);
      const r = await api.put(`/scores/baseball/${gameId}`, {
        inning_scores: scores, home_total_runs: homeTotal, away_total_runs: awayTotal, ...extra,
      });
      onScoreUpdate(r.data);
    } finally { setBusy(false); }
  };

  const setOuts = async (n: number) => {
    setBusy(true);
    try { const r = await api.put(`/scores/baseball/${gameId}`, { outs: n }); onScoreUpdate(r.data); }
    finally { setBusy(false); }
  };

  const endHalf = async () => {
    const nextTop = score.is_top_inning ? 0 : 1;
    const nextInn = score.is_top_inning ? score.current_inning : score.current_inning + 1;
    setBusy(true);
    try { const r = await api.put(`/scores/baseball/${gameId}`, { outs: 0, is_top_inning: nextTop, current_inning: nextInn }); onScoreUpdate(r.data); }
    finally { setBusy(false); }
  };

  const completeGame = async () => {
    if (!window.confirm('Mark this game as completed?')) return;
    setBusy(true);
    try {
      const homeTotal = Object.values(innVals).reduce((s, v) => s + v.home, 0);
      const awayTotal = Object.values(innVals).reduce((s, v) => s + v.away, 0);
      await api.post(`/games/${gameId}/complete`, { home_final_score: homeTotal, away_final_score: awayTotal, player_records: [] });
    } catch (e: any) { alert(e?.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  const isTop = !!score.is_top_inning;
  const innArr = Array.from({ length: innings }, (_, i) => i + 1);
  const homeTotal = Object.values(innVals).reduce((s, v) => s + v.home, 0);
  const awayTotal = Object.values(innVals).reduce((s, v) => s + v.away, 0);

  const cellInput = (team: 'home' | 'away', inn: number) => (
    <input
      key={`${team}-${inn}`}
      type="number" min={0}
      value={innVals[inn]?.[team] ?? 0}
      onChange={e => setInnVals(p => ({ ...p, [inn]: { ...p[inn], [team]: Math.max(0, Number(e.target.value)) } }))}
      style={{ width: '36px', textAlign: 'center', background: inn === score.current_inning ? 'rgba(249,115,22,0.12)' : 'var(--surface-sunken)', border: `1px solid ${inn === score.current_inning ? '#F97316' : 'var(--hairline)'}`, borderRadius: '5px', color: 'var(--ink-1)', padding: '4px 2px', fontSize: '14px', fontWeight: 700 }}
    />
  );

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid #F97316', borderRadius: '14px', padding: '16px', marginTop: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', letterSpacing: '1px', textTransform: 'uppercase' }}>⚾ Scoring</div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Top / Bottom toggle */}
          <button onClick={() => api.put(`/scores/baseball/${gameId}`, { is_top_inning: isTop ? 0 : 1 }).then(r => onScoreUpdate(r.data))}
            disabled={busy} style={{ fontSize: '13px', fontWeight: 800, padding: '5px 12px', borderRadius: '7px', border: '1px solid #F9731644', background: 'rgba(249,115,22,0.1)', color: '#F97316', cursor: 'pointer' }}>
            {isTop ? '▲ Top' : '▼ Bot'} {score.current_inning}
          </button>
          <button onClick={endHalf} disabled={busy}
            style={{ fontSize: '12px', padding: '5px 12px', borderRadius: '7px', border: '1px solid #EAB30844', background: 'rgba(234,179,8,0.08)', color: '#EAB308', cursor: 'pointer', fontWeight: 700 }}>
            Next Half ⏭
          </button>
        </div>
      </div>

      {/* Outs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Outs</span>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[0, 1, 2].map(n => (
            <button key={n} onClick={() => setOuts(n)} disabled={busy}
              style={{ width: 32, height: 32, borderRadius: '50%', border: `2px solid ${(score.outs || 0) >= n + 1 ? '#EF4444' : 'var(--hairline)'}`, background: (score.outs || 0) >= n + 1 ? 'rgba(239,68,68,0.2)' : 'transparent', cursor: 'pointer', fontWeight: 900, fontSize: '13px', color: (score.outs || 0) >= n + 1 ? '#EF4444' : 'var(--ink-4)' }}>
              {n + 1}
            </button>
          ))}
        </div>
        <button onClick={() => setOuts(0)} disabled={busy || score.outs === 0}
          style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '5px', border: '1px solid var(--hairline)', background: 'transparent', color: 'var(--ink-4)', cursor: 'pointer' }}>
          Reset
        </button>
      </div>

      {/* Inning-by-inning scoreboard */}
      <div style={{ overflowX: 'auto', marginBottom: '12px' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr>
              <th style={{ padding: '4px 10px 4px 0', color: 'var(--ink-4)', fontSize: '10px', textAlign: 'left', whiteSpace: 'nowrap' }}>Team</th>
              {innArr.map(i => <th key={i} style={{ padding: '4px 3px', color: i === score.current_inning ? '#F97316' : 'var(--ink-4)', fontSize: '10px', textAlign: 'center', minWidth: '40px' }}>{i}</th>)}
              <th style={{ padding: '4px 6px', color: 'var(--ink-1)', fontSize: '10px', fontWeight: 900, textAlign: 'center' }}>R</th>
            </tr>
          </thead>
          <tbody>
            {(['away', 'home'] as const).map(team => (
              <tr key={team}>
                <td style={{ padding: '4px 10px 4px 0', color: 'var(--ink-3)', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {team === 'away' ? awayTeam.split(' ').pop() : homeTeam.split(' ').pop()}
                </td>
                {innArr.map(i => <td key={i} style={{ padding: '3px' }}>{cellInput(team, i)}</td>)}
                <td style={{ padding: '4px 6px', fontSize: '16px', fontWeight: 900, color: 'var(--ink-1)', textAlign: 'center' }}>
                  {team === 'away' ? awayTotal : homeTotal}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add inning + Save + Complete */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => setInnings(n => n + 1)}
          style={{ fontSize: '12px', padding: '7px 12px', borderRadius: '7px', border: '1px solid var(--hairline)', background: 'transparent', color: 'var(--ink-3)', cursor: 'pointer', fontWeight: 700 }}>
          + Inning
        </button>
        <button onClick={() => save()} disabled={busy}
          style={{ fontSize: '12px', padding: '7px 16px', borderRadius: '7px', border: 'none', background: '#F97316', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
          {busy ? 'Saving…' : 'Save Scores'}
        </button>
        <button onClick={completeGame} disabled={busy}
          style={{ fontSize: '12px', padding: '7px 12px', borderRadius: '7px', border: '1px solid #22C55E44', background: 'rgba(34,197,94,0.08)', color: '#22C55E', cursor: 'pointer', fontWeight: 700, marginLeft: 'auto' }}>
          ✓ Final
        </button>
      </div>
    </div>
  );
}; // ← end replacement

// ── Stub to consume old body (deleted below) ────────────────────────────────

// ── Main Watch page ───────────────────────────────────────────────────────────
const Watch = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame]               = useState<Game | null>(null);
  const [score, setScore]             = useState<BaseballScore | SoccerScore | BasketballScore | null>(null);
  const [loading, setLoading]         = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [timerMs, setTimerMs]         = useState(0);
  const [teamPlayers, setTeamPlayers]         = useState<TeamPlayer[]>([]);
  const [ourSide, setOurSide]                 = useState<'home' | 'away' | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const socketRef     = useRef<Socket | null>(null);
  const timerRef      = useRef<ReturnType<typeof setInterval> | null>(null);

  // AI Analysis state
  const [recording, setRecording]         = useState<GameVideo | null>(null);
  const [reanalyzing, setReanalyzing]     = useState(false);
  const analysisPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const user      = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const isAdmin   = user?.role === 'admin';
  const isManager = !isAdmin && Array.isArray(user?.managed_team_ids) && !!game?.team_id
    && user.managed_team_ids.includes(game.team_id);
  const canUploadHighlight = isAdmin || isManager;

  // Highlight reel state
  const [highlights, setHighlights]               = useState<GameVideo[]>([]);
  const [selectedHighlight, setSelectedHighlight] = useState<GameVideo | null>(null);
  const [showHighlightUpload, setShowHighlightUpload] = useState(false);

  // Score overlay toggle — admins can hide it (e.g. to unblock the view)
  const [showOverlay, setShowOverlay] = useState(true);
  // Scoring Mode: hides the video stream so the scoring panel fills the screen
  const [scoringMode, setScoringMode] = useState(false);

  // ── Live overlay settings (synced from game record via socket) ────────────
  const [overlayOn,      setOverlayOn]      = useState(false);
  const [tickerOn,       setTickerOn]       = useState(false);
  const [overlaySponsorId, setOverlaySponsorId] = useState<string | null>(null);
  const [overlaySponsor, setOverlaySponsor] = useState<any | null>(null);
  const [customTicker,   setCustomTicker]   = useState<string[]>([]);
  const [cornerLogoUrl,  setCornerLogoUrl]  = useState<string | null>(null);
  const [overlayBusy,    setOverlayBusy]    = useState(false);
  const [publicLinkCopied, setPublicLinkCopied] = useState(false);

  // ── Home team logo (from linked team record) ──────────────────────────────
  const [homeLogo, setHomeLogo] = useState<string | null>(null);

  // ── Track whether the live VideoPlayer is in fullscreen mode ──────────────
  // Used to remove overflow:hidden from the wrapper div so iOS doesn't clip
  // the position:fixed fullscreen element to its parent bounds.
  const [videoFullscreen, setVideoFullscreen] = useState(false);

  // Sponsor placements for this game
  const { fieldBug, lowerThird, seasonCard } = useSponsorPlacements(gameId);

  // CSS fullscreen — Watch.tsx owns this so the button is always visible
  const videoWrapperRef = useRef<HTMLDivElement>(null);

  const tickTimer = useCallback((s: SoccerScore) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTimerMs(computeElapsedMs(s));
    if (s.timer_running === 1) {
      timerRef.current = setInterval(() => setTimerMs(computeElapsedMs(s)), 1000);
    }
  }, []);

  useEffect(() => {
    const fetchGame = async () => {
      try {
        const res = await api.get(`/games/${gameId}`);
        const { score: gameScore, ...gameData } = res.data;
        setGame(gameData);
        if (gameScore) {
          setScore(gameScore);
          if (gameData.sport === 'soccer') tickTimer(gameScore as SoccerScore);
        }

        // Initialise overlay state from game record
        setOverlayOn(!!gameData.overlay_enabled);
        setTickerOn(!!gameData.ticker_enabled);
        setOverlaySponsorId(gameData.overlay_sponsor_id ?? null);
        // Custom ticker messages (stored as JSON array)
        try { setCustomTicker(JSON.parse(gameData.custom_ticker || '[]')); } catch { setCustomTicker([]); }
        // Corner logo / ad
        setCornerLogoUrl((gameData as any).corner_logo_url ?? null);

        // Home team logo is now included directly in the game response (no extra fetch needed,
        // no auth/team-membership issues). Apply mediaUrl() so Capacitor native resolves it.
        setHomeLogo(mediaUrl((gameData as any).home_logo_url ?? null) || null);

        // Fetch overlay sponsor if set
        if (gameData.overlay_sponsor_id) {
          try {
            const sRes = await api.get(`/sponsors/${gameData.overlay_sponsor_id}`);
            setOverlaySponsor(sRes.data ?? null);
          } catch {}
        }

        // Fetch roster + determine our side (admin-only feature)
        if (isAdmin && gameData.team_id && gameData.sport === 'soccer') {
          try {
            const [playersRes, teamsRes] = await Promise.all([
              api.get('/players', { params: { team_id: gameData.team_id } }),
              api.get('/teams'),
            ]);
            setTeamPlayers(playersRes.data || []);
            const myTeam = (teamsRes.data || []).find((t: any) => t.id === gameData.team_id);
            if (myTeam) {
              const normalize = (s: string) =>
                s.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim();

              // Character bigrams similarity (Sørensen–Dice coefficient)
              const bigrams = (s: string): Set<string> => {
                const bg = new Set<string>();
                const t = s.replace(/\s/g, '');
                for (let i = 0; i < t.length - 1; i++) bg.add(t.slice(i, i + 2));
                return bg;
              };
              const bigramSim = (a: string, b: string): number => {
                const ab = bigrams(a); const bb = bigrams(b);
                if (!ab.size && !bb.size) return 0;
                let inter = 0;
                ab.forEach(bg => { if (bb.has(bg)) inter++; });
                return (2 * inter) / (ab.size + bb.size);
              };

              // Token Jaccard similarity
              const tokenSim = (a: string, b: string): number => {
                const at = new Set(a.split(/\s+/).filter(Boolean));
                const bt = new Set(b.split(/\s+/).filter(Boolean));
                if (!at.size && !bt.size) return 0;
                let inter = 0;
                at.forEach(t => { if (bt.has(t)) inter++; });
                return inter / (at.size + bt.size - inter);
              };

              // Word-overlap: fraction of shorter name's words that appear in the longer name
              const wordOverlap = (a: string, b: string): number => {
                const at = a.split(/\s+/).filter(Boolean);
                const bt = b.split(/\s+/).filter(Boolean);
                const [shorter, longer] = at.length <= bt.length ? [at, bt] : [bt, at];
                if (!shorter.length) return 0;
                const hits = shorter.filter(w => longer.some(lw => lw.includes(w) || w.includes(lw))).length;
                return hits / shorter.length;
              };

              // Combined: prefer exact/contains first, then best of bigram / token / word-overlap
              const teamSim = (myName: string, opponent: string): number => {
                const a = normalize(myName); const b = normalize(opponent);
                if (a === b) return 1;
                if (a.includes(b) || b.includes(a)) return 0.9;
                return Math.max(bigramSim(a, b), tokenSim(a, b), wordOverlap(a, b));
              };

              const myName  = myTeam.name.trim();
              const homeSim = teamSim(myName, gameData.home_team || '');
              const awaySim = teamSim(myName, gameData.away_team || '');

              // Always pick the better match — user can override with the selector.
              // Even a low-confidence guess is better than silently defaulting to home.
              setOurSide(homeSim >= awaySim ? 'home' : 'away');
            }
          } catch {}
        }
      } catch (err: any) {
        if (err.response?.status === 403) {
          setAccessDenied(true);
        } else {
          setGame(null);
        }
      } finally { setLoading(false); }
    };
    fetchGame();

    const SERVER_URL = process.env.REACT_APP_SERVER_URL ?? '';
    const socket = io(SERVER_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    // Stable session ID: survives socket reconnects within same tab, resets on tab close.
    // This prevents each reconnect from inflating the viewer count.
    let sessionId = sessionStorage.getItem('viewer_session_id');
    if (!sessionId) {
      sessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('viewer_session_id', sessionId);
    }

    const joinRoom = () => socket.emit('join_game', { gameId, sessionId });
    socket.on('connect', joinRoom);
    joinRoom();

    socket.on('score_update', (data: any) => {
      if (data.gameId === gameId) {
        setScore(data.score);
        if (data.sport === 'soccer') tickTimer(data.score as SoccerScore);
      }
    });
    socket.on('viewer_count', (c: number) => setViewerCount(c));

    // Live overlay state pushed from admin → all viewers update instantly
    socket.on('overlay_update', (data: any) => {
      if (data.gameId === gameId) {
        setOverlayOn(!!data.overlay_enabled);
        setTickerOn(!!data.ticker_enabled);
        setOverlaySponsorId(data.overlay_sponsor_id ?? null);
        setOverlaySponsor(data.sponsor ?? null);
        if (data.custom_ticker !== undefined) {
          try { setCustomTicker(JSON.parse(data.custom_ticker || '[]')); } catch { setCustomTicker([]); }
        }
        if (data.corner_logo_url !== undefined) setCornerLogoUrl(data.corner_logo_url ?? null);
      }
    });

    // When admin completes the game, all viewers see it transition to completed
    socket.on('game_completed', (data: any) => {
      if (data.gameId === gameId) {
        setGame(g => g ? { ...g, status: 'completed' } : g);
        setShowCompleteModal(false);
      }
    });

    return () => {
      socket.emit('leave_game', { gameId, sessionId });
      socket.disconnect();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameId, tickTimer]);

  // ── Fetch recording + highlights ───────────────────────────────────────────
  const fetchVideos = useCallback(async (gId: string) => {
    try {
      const r = await api.get(`/videos`, { params: { game_id: gId } });
      const all = r.data as GameVideo[];
      const rec = all.find(v => v.video_type === 'recording');
      setRecording(rec || null);
      setHighlights(all.filter(v => v.video_type === 'highlight'));
      return rec;
    } catch { return undefined; }
  }, []);

  // Alias for backward compat with polling effect below
  const fetchRecording = fetchVideos;

  // Load videos once the game is known; poll if analysis is processing
  useEffect(() => {
    if (!game?.id || game.status !== 'completed') return;
    fetchVideos(game.id);
  }, [game?.id, game?.status, fetchVideos]);

  useEffect(() => {
    if (!recording || !game?.id) return;
    if (recording.analysis_status === 'processing') {
      if (analysisPollRef.current) clearInterval(analysisPollRef.current);
      analysisPollRef.current = setInterval(async () => {
        const rec = await fetchRecording(game.id);
        if (rec?.analysis_status !== 'processing') {
          if (analysisPollRef.current) clearInterval(analysisPollRef.current);
        }
      }, 10000);
    } else {
      if (analysisPollRef.current) clearInterval(analysisPollRef.current);
    }
    return () => { if (analysisPollRef.current) clearInterval(analysisPollRef.current); };
  }, [recording?.analysis_status, game?.id, fetchRecording]);

  if (loading) return (
    <div className="container" style={{ textAlign: 'center', padding: '100px 20px', color: 'var(--ink-4)' }}>
      <div style={{ fontSize: '40px', marginBottom: '14px' }}>⏳</div>
      Loading game...
    </div>
  );

  if (accessDenied) return (
    <div className="container" style={{ textAlign: 'center', padding: '100px 20px' }}>
      <div style={{ fontSize: '60px', marginBottom: '16px' }}>🔒</div>
      <h2 style={{ color: 'var(--ink-1)', marginBottom: '8px' }}>Access Restricted</h2>
      <p style={{ color: 'var(--ink-3)', marginBottom: '24px', maxWidth: '380px', margin: '0 auto 24px' }}>
        You don't have access to this game. Contact your league administrator to update your team access.
      </p>
      <Link to="/games" className="btn btn-secondary">← Back to Games</Link>
    </div>
  );

  if (!game) return (
    <div className="container" style={{ textAlign: 'center', padding: '100px 20px' }}>
      <div style={{ fontSize: '60px', marginBottom: '16px' }}>🚫</div>
      <h3 style={{ marginBottom: '14px' }}>Game not found</h3>
      <Link to="/" className="btn btn-secondary">← Back to Games</Link>
    </div>
  );

  const HLS_BASE      = process.env.REACT_APP_HLS_URL ?? '/hls';
  const hlsUrl        = mediaUrl(`${HLS_BASE}/live/${game.stream_key}/index.m3u8`);
  const isSoccer      = game.sport === 'soccer';
  const isBaseball    = game.sport === 'baseball' || game.sport === 'softball';
  const isBasketball  = game.sport === 'basketball';
  const soccerScore      = isSoccer     && score ? score as SoccerScore     : null;
  const baseballScore    = isBaseball   && score ? score as BaseballScore   : null;
  const basketballScore  = isBasketball && score ? score as unknown as BasketballScore : null;

  const videoOverlay = (game.status === 'live' && soccerScore)
    ? <SoccerOverlay score={soccerScore} homeTeam={game.home_team} awayTeam={game.away_team} timerMs={timerMs} />
    : undefined;

  const baseballOverlay = (game.status === 'live' && baseballScore)
    ? <BaseballOverlay score={baseballScore} homeTeam={game.home_team} awayTeam={game.away_team} />
    : undefined;

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <Link to="/" style={{ color: 'var(--ink-3)', fontSize: '14px' }}>← Games</Link>
        <span style={{ color: 'var(--hairline)' }}>/</span>
        <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink-1)' }}>{game.title}</span>
        {game.status === 'live' && <span className="live-badge">Live</span>}
        {game.status === 'completed' && <span className="status-pill status-completed">Final</span>}
      </div>

      <div className="watch-grid">
        <div>
          <div className="watch-video-sticky">
          {game.status === 'live' ? (
            <div ref={videoWrapperRef}>
              {/* Scoring Mode toggle — admin soccer only */}
              {isAdmin && isSoccer && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                  <button
                    onClick={() => setScoringMode(v => !v)}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 700,
                      border: `1px solid ${scoringMode ? '#F97316' : 'var(--hairline)'}`,
                      background: scoringMode ? 'rgba(249,115,22,0.12)' : 'var(--surface-muted)',
                      color: scoringMode ? '#F97316' : 'var(--ink-3)',
                      touchAction: 'manipulation', transition: 'all 0.15s',
                    }}>
                    {scoringMode ? '📹 Show Stream' : '⚽ Scoring Mode'}
                  </button>
                </div>
              )}

              {/* Video — hidden in Scoring Mode so the full screen is available for controls */}
              {!scoringMode && (
                <div style={{
                  borderRadius: '10px',
                  // Remove overflow:hidden while fullscreen — older iOS clips
                  // position:fixed children to the ancestor's overflow bounds,
                  // causing the "top cut off / menu bar visible" symptoms.
                  overflow: videoFullscreen ? 'visible' : 'hidden',
                }}>
                  <VideoPlayer streamUrl={hlsUrl} onFullscreenChange={setVideoFullscreen}>
                    {/* ── ESPN corner score bug (top-left, inside video) ──
                         NOTE: we do NOT gate on soccerScore being non-null.
                         The bug shows with 0–0 defaults until the score loads,
                         so overlay_update socket events immediately display the
                         bug even before any score events have been logged.       */}
                    {overlayOn && isSoccer && (
                      <ScoreBug
                        homeTeam={game.home_team}
                        awayTeam={game.away_team}
                        homeLogo={homeLogo}
                        awayLogo={mediaUrl((game as any).away_logo_url ?? null) || null}
                        homeScore={soccerScore?.home_score ?? 0}
                        awayScore={soccerScore?.away_score ?? 0}
                        status={soccerScore?.status ?? 'not_started'}
                        timerMs={timerMs}
                        sponsor={overlaySponsor}
                      />
                    )}

                    {/* ── Top-right corner logo / ad bug ── */}
                    {cornerLogoUrl && <CornerLogoBug logoUrl={cornerLogoUrl} />}

                    {/* ── Stats ticker (bottom, inside video) ── */}
                    {tickerOn && isSoccer && (soccerScore?.events?.length || customTicker.length) > 0 && (
                      <EventTicker
                        events={soccerScore?.events ?? []}
                        homeTeam={game.home_team}
                        awayTeam={game.away_team}
                        customItems={customTicker}
                      />
                    )}

                    {/* ── Baseball score bug (existing, moved inside) ── */}
                    {showOverlay && baseballOverlay && (
                      <div style={{
                        position: 'absolute', bottom: 56, right: 12, zIndex: 20, pointerEvents: 'none',
                      }}>
                        {baseballOverlay}
                      </div>
                    )}

                    {/* ── Field sponsor bug (existing) ── */}
                    {fieldBug && <FieldSponsorBug sponsor={fieldBug} />}
                  </VideoPlayer>

                  {/* Score visibility toggle — baseball/basketball admin */}
                  {isAdmin && !isSoccer && (baseballScore || basketballScore) && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 8px', background: 'rgba(0,0,0,0.85)', borderRadius: '0 0 10px 10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <button onClick={() => setShowOverlay(v => !v)}
                        style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: '#fff', fontSize: '12px', fontWeight: 600, padding: '5px 12px', cursor: 'pointer' }}>
                        {showOverlay ? '🙈 Hide Score' : '📊 Show Score'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Lower-third sponsor banner — beneath live video */}
              {lowerThird && <LowerThirdSponsor sponsor={lowerThird} />}
            </div>
          ) : game.status === 'scheduled' ? (
            <div style={{
              background: 'var(--surface)', borderRadius: '12px', aspectRatio: '16/9',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              border: '1px solid var(--hairline)', padding: '32px', textAlign: 'center',
            }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%', marginBottom: '20px',
                background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px',
              }}>📅</div>
              <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px' }}>Stream Not Started</h3>
              <p style={{ color: 'var(--ink-3)', fontSize: '14px', lineHeight: 1.5 }}>
                {parseGameTime(game.scheduled_at).toLocaleDateString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
                })}
              </p>
            </div>
          ) : recording ? (
            <>
              {/* ── Completed game — recording with FT overlays ── */}
              <VideoPlayer streamUrl={(() => {
                const fp = recording.file_path ?? '';
                if (!fp) return '';
                // Wasabi proxy paths (/api/...) and absolute URLs need no prefix.
                // NMS auto-linked recordings are stored as /live/key/file.mp4 and
                // must be served via the /hls nginx proxy.
                return (fp.startsWith('/api/') || fp.startsWith('http')) ? fp : `${HLS_BASE}${fp}`;
              })()}>
                {/* FT score bug */}
                {isSoccer && (
                  <ScoreBug
                    homeTeam={game.home_team}
                    awayTeam={game.away_team}
                    homeLogo={homeLogo}
                    awayLogo={mediaUrl((game as any).away_logo_url ?? null) || null}
                    homeScore={(game as any).home_final_score ?? soccerScore?.home_score ?? 0}
                    awayScore={(game as any).away_final_score ?? soccerScore?.away_score ?? 0}
                    status="full_time"
                    timerMs={0}
                    sponsor={overlaySponsor}
                  />
                )}
                {/* Stats ticker */}
                {isSoccer && soccerScore?.events && soccerScore.events.length > 0 && (
                  <EventTicker
                    events={soccerScore.events}
                    homeTeam={game.home_team}
                    awayTeam={game.away_team}
                  />
                )}
                {fieldBug && <FieldSponsorBug sponsor={fieldBug} />}
              </VideoPlayer>

              {/* ── Goal summary strip ── */}
              {isSoccer && (soccerScore?.events ?? []).some(e => e.event_type === 'goal') && (
                <div style={{
                  background: 'rgba(0,0,0,0.82)',
                  borderRadius: '0 0 10px 10px',
                  padding: '9px 16px',
                  borderTop: '2px solid #F97316',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '5px 20px',
                  alignItems: 'center',
                }}>
                  {[...(soccerScore?.events ?? [])]
                    .filter(e => e.event_type === 'goal')
                    .sort((a, b) => a.minute - b.minute)
                    .map((ev, i) => (
                      <span key={ev.id || i} style={{ fontSize: 12, color: '#E8F0FE', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        ⚽ {ev.minute}′{ev.player_name ? ` ${ev.player_name}` : ''}{' '}
                        <span style={{ color: 'rgba(255,255,255,0.48)', fontWeight: 400 }}>
                          ({ev.team === 'home' ? game.home_team : game.away_team})
                        </span>
                      </span>
                    ))
                  }
                </div>
              )}
            </>
          ) : (
            <div style={{
              background: 'var(--surface)', borderRadius: '12px', aspectRatio: '16/9',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              border: '1px solid var(--hairline)',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏁</div>
              <h3 style={{ fontSize: '18px', fontWeight: 800 }}>Game Complete</h3>
            </div>
          )}
          </div>

          <div style={{
            marginTop: '16px', padding: '16px 20px',
            background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span className={`sport-pill sport-${game.sport}`}>
                  {SPORT_ICONS[game.sport]} {game.sport}
                </span>
                {game.location && <span style={{ fontSize: '13px', color: 'var(--ink-3)' }}>📍 {game.location}</span>}
              </div>
              {game.status === 'live' && viewerCount > 0 && (
                <span style={{
                  fontSize: '12px', color: '#F97316', fontWeight: 600,
                  background: 'rgba(249,115,22,0.08)', padding: '4px 10px', borderRadius: '20px',
                  border: '1px solid rgba(249,115,22,0.2)',
                }}>
                  👁 {viewerCount} watching
                </span>
              )}
            </div>
            <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
              <span style={{ fontSize: '20px', fontWeight: 900 }}>{game.away_team}</span>
              <span style={{ fontSize: '13px', color: 'var(--ink-4)', fontWeight: 700 }}>@</span>
              <span style={{ fontSize: '20px', fontWeight: 900 }}>{game.home_team}</span>
            </div>
            {/* ── Public share link ── */}
            {(() => {
              const publicUrl = `${window.location.origin}/live/${gameId}`;
              const copyPublicLink = () => {
                navigator.clipboard.writeText(publicUrl).then(() => {
                  setPublicLinkCopied(true);
                  setTimeout(() => setPublicLinkCopied(false), 2000);
                });
              };
              return (
                <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', borderTop: '1px solid var(--hairline)', paddingTop: '12px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--ink-3)', fontWeight: 600, whiteSpace: 'nowrap' }}>🔗 Public link:</span>
                  <code style={{ flex: 1, fontSize: '11px', color: 'var(--ink-2)', background: 'var(--surface-2)', padding: '4px 8px', borderRadius: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {publicUrl}
                  </code>
                  <button
                    onClick={copyPublicLink}
                    className="btn btn-secondary"
                    style={{ fontSize: '12px', padding: '4px 12px', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {publicLinkCopied ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
              );
            })()}

            {game.description && (
              <p style={{ marginTop: '12px', fontSize: '13px', color: 'var(--ink-3)', lineHeight: 1.6, borderTop: '1px solid var(--hairline)', paddingTop: '12px' }}>
                {game.description}
              </p>
            )}
          </div>

          {isAdmin && isSoccer && game.status === 'live' && soccerScore && (
            <SoccerMatchControl
              gameId={gameId!}
              score={soccerScore}
              timerMs={timerMs}
              homeTeam={game.home_team}
              awayTeam={game.away_team}
              teamPlayers={teamPlayers}
              ourSide={ourSide}
              onSetOurSide={setOurSide}
              onUpdate={(s) => { setScore(s); tickTimer(s); }}
              onCompleteGame={() => setShowCompleteModal(true)}
            />
          )}

          {/* ── Live Overlay Admin Controls (soccer live only) ───────────── */}
          {isAdmin && isSoccer && game.status === 'live' && (
            <OverlayControlPanel
              gameId={gameId!}
              overlayOn={overlayOn}
              tickerOn={tickerOn}
              overlaySponsorId={overlaySponsorId}
              overlaySponsor={overlaySponsor}
              customTicker={customTicker}
              cornerLogoUrl={cornerLogoUrl}
              busy={overlayBusy}
              onToggleOverlay={async (val) => {
                setOverlayBusy(true);
                try {
                  await api.put(`/games/${gameId}`, { overlay_enabled: val });
                  setOverlayOn(val);
                } catch {}
                finally { setOverlayBusy(false); }
              }}
              onToggleTicker={async (val) => {
                setOverlayBusy(true);
                try {
                  await api.put(`/games/${gameId}`, { ticker_enabled: val });
                  setTickerOn(val);
                } catch {}
                finally { setOverlayBusy(false); }
              }}
              onChangeSponsor={async (sponsorId, sponsor) => {
                setOverlayBusy(true);
                try {
                  await api.put(`/games/${gameId}`, { overlay_sponsor_id: sponsorId || null });
                  setOverlaySponsorId(sponsorId || null);
                  setOverlaySponsor(sponsor);
                } catch {}
                finally { setOverlayBusy(false); }
              }}
              onChangeCustomTicker={async (items) => {
                setOverlayBusy(true);
                try {
                  await api.put(`/games/${gameId}`, { custom_ticker: JSON.stringify(items) });
                  setCustomTicker(items);
                } catch {}
                finally { setOverlayBusy(false); }
              }}
              onChangeCornerLogo={async (url) => {
                setOverlayBusy(true);
                try {
                  await api.put(`/games/${gameId}`, { corner_logo_url: url || null });
                  setCornerLogoUrl(url);
                } catch {}
                finally { setOverlayBusy(false); }
              }}
            />
          )}

          {(isAdmin || isManager) && isBaseball && game.status === 'live' && baseballScore && (
            <BaseballScoringControls
              gameId={gameId!}
              score={baseballScore}
              homeTeam={game.home_team}
              awayTeam={game.away_team}
              onScoreUpdate={(s) => setScore(s)}
            />
          )}

          {isAdmin && isBasketball && game.status === 'live' && basketballScore && (
            <BasketballScoringControls
              gameId={gameId!}
              score={basketballScore}
              homeTeam={game.home_team}
              awayTeam={game.away_team}
              onScoreUpdate={(s) => setScore(s)}
            />
          )}

          {/* Complete-game modal — rendered inside container but fixed-position overlay */}
          {showCompleteModal && soccerScore && (
            <CompleteGameModal
              gameId={gameId!}
              score={soccerScore}
              teamPlayers={teamPlayers}
              ourSide={ourSide}
              homeTeam={game.home_team}
              awayTeam={game.away_team}
              onClose={() => setShowCompleteModal(false)}
              onCompleted={() => {
                setShowCompleteModal(false);
                setGame(g => g ? { ...g, status: 'completed' } : g);
              }}
            />
          )}
        </div>

        <div>
          {/* Season sponsor card — sidebar */}
          {seasonCard && (
            <div style={{ marginBottom: 20 }}>
              <SeasonSponsorCard sponsor={seasonCard} />
            </div>
          )}

          <div style={{
            fontSize: '11px', fontWeight: 700, letterSpacing: '1.2px', color: 'var(--ink-4)',
            textTransform: 'uppercase', marginBottom: '12px',
          }}>
            Box Score
          </div>
          {score ? (
            isBaseball ? (
              <BaseballBoxScore
                score={score as BaseballScore}
                homeTeam={game.home_team}
                awayTeam={game.away_team}
                sport={game.sport as 'baseball' | 'softball'}
              />
            ) : isBasketball ? (
              <BasketballBoxScore
                score={score as BasketballScore}
                homeTeam={game.home_team}
                awayTeam={game.away_team}
              />
            ) : (
              <SoccerBoxScore
                score={score as SoccerScore}
                homeTeam={game.home_team}
                awayTeam={game.away_team}
                timerMs={timerMs}
              />
            )
          ) : (
            <div style={{
              padding: '40px 20px', textAlign: 'center', color: 'var(--ink-4)',
              background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px',
            }}>
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>📊</div>
              <p style={{ fontSize: '13px' }}>
                {game.status === 'scheduled' ? 'Scoreboard appears when the game starts.' : 'No score data available.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Highlight Reel (completed games) ── */}
      {game.status === 'completed' && (
        <div style={{ marginTop: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: 32, height: 32, borderRadius: '8px', background: 'rgba(249,115,22,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🎬</div>
              <span style={{ fontWeight: 800, fontSize: '17px', color: 'var(--ink-1)' }}>Highlight Reel</span>
              {highlights.length > 0 && <span style={{ fontSize: '12px', color: 'var(--ink-4)', background: 'var(--surface-muted)', padding: '2px 8px', borderRadius: '10px' }}>{highlights.length}</span>}
            </div>
            {canUploadHighlight && (
              <button
                onClick={() => setShowHighlightUpload(true)}
                style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid rgba(249,115,22,0.3)', background: 'rgba(249,115,22,0.1)', color: '#F97316', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                ⬆️ Upload Highlight
              </button>
            )}
          </div>

          {/* Playing a selected highlight */}
          {selectedHighlight && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink-1)' }}>{selectedHighlight.title}</div>
                <button onClick={() => setSelectedHighlight(null)} style={{ background: 'none', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
              </div>
              <VideoPlayer streamUrl={selectedHighlight.file_path} />
            </div>
          )}

          {highlights.length === 0 ? (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px', padding: '48px', textAlign: 'center', color: 'var(--ink-4)' }}>
              <div style={{ fontSize: '36px', marginBottom: '10px' }}>🎬</div>
              <div style={{ fontSize: '14px' }}>
                No highlights uploaded yet.
                {canUploadHighlight && ' Click "Upload Highlight" to add one.'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
              {highlights.map(h => (
                <div
                  key={h.id}
                  onClick={() => setSelectedHighlight(s => s?.id === h.id ? null : h)}
                  style={{
                    background: 'var(--surface)', border: `1px solid ${selectedHighlight?.id === h.id ? '#F97316' : 'var(--hairline)'}`,
                    borderRadius: '10px', overflow: 'hidden', cursor: 'pointer',
                    transition: 'border-color 0.15s, transform 0.1s',
                    transform: selectedHighlight?.id === h.id ? 'scale(0.98)' : 'scale(1)',
                  }}
                >
                  {/* Thumbnail placeholder */}
                  <div style={{ aspectRatio: '16/9', background: 'var(--surface-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    {h.thumbnail_url
                      ? <img src={h.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ fontSize: '32px' }}>🎬</div>
                    }
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', opacity: selectedHighlight?.id === h.id ? 0 : 1, transition: 'opacity 0.15s' }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(249,115,22,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>▶</div>
                    </div>
                  </div>
                  <div style={{ padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--ink-1)', marginBottom: '2px' }}>{h.title}</div>
                      <div style={{ fontSize: '11px', color: 'var(--ink-4)' }}>{new Date(h.uploaded_at).toLocaleDateString()}</div>
                    </div>
                    {canUploadHighlight && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm('Delete this highlight?')) return;
                          try {
                            await api.delete(`/videos/${h.id}`);
                            setHighlights(hs => hs.filter(x => x.id !== h.id));
                            if (selectedHighlight?.id === h.id) setSelectedHighlight(null);
                          } catch {}
                        }}
                        style={{ background: 'none', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: '16px', padding: '0 2px', flexShrink: 0 }}
                        title="Delete highlight"
                      >✕</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── AI Analysis Panel (completed games only) ── */}
      {game.status === 'completed' && (
        <div style={{ marginTop: '32px' }}>
          {/* Section header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '12px', marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '8px',
                background: 'linear-gradient(135deg, rgba(168,85,247,0.25), rgba(249,115,22,0.25))',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px',
              }}>🤖</div>
              <span style={{ fontWeight: 800, fontSize: '17px', color: 'var(--ink-1)' }}>AI Coaching Analysis</span>
            </div>
            {isAdmin && recording && recording.analysis_status !== 'processing' && (
              <button
                onClick={async () => {
                  if (!recording) return;
                  setReanalyzing(true);
                  try {
                    await api.post(`/videos/${recording.id}/analyze`);
                    setRecording(r => r ? { ...r, analysis_status: 'processing' } : r);
                  } catch (e: any) {
                    alert(e.response?.data?.error || 'Failed to start analysis');
                  } finally { setReanalyzing(false); }
                }}
                disabled={reanalyzing}
                style={{
                  padding: '7px 14px', borderRadius: '8px', border: '1px solid rgba(168,85,247,0.3)',
                  background: 'rgba(168,85,247,0.1)', color: '#A78BFA',
                  fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                }}
              >
                {reanalyzing ? '⏳ Starting…' : recording.analysis_status === 'completed' ? '↺ Re-analyze' : '✨ Analyze'}
              </button>
            )}
          </div>

          {/* No recording uploaded yet */}
          {!recording && (
            <div style={{
              background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px',
              padding: '48px', textAlign: 'center', color: 'var(--ink-4)',
            }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎬</div>
              <div style={{ fontSize: '14px' }}>
                No recording uploaded for this game yet.{isAdmin && ' Upload one via Admin → Videos to enable AI analysis.'}
              </div>
            </div>
          )}

          {/* Recording exists — show analysis state */}
          {recording && (() => {
            const status = recording.analysis_status;

            // ── Not yet triggered ────────────────────────────────────────────
            if (status === null) {
              return (
                <div style={{
                  background: 'var(--surface)', border: '1px solid var(--hairline)',
                  borderRadius: '12px', padding: '48px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: '40px', marginBottom: '14px' }}>🤖</div>
                  <div style={{ fontWeight: 800, fontSize: '15px', color: 'var(--ink-1)', marginBottom: '6px' }}>
                    No analysis yet for this game
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--ink-3)', marginBottom: isAdmin ? '20px' : 0 }}>
                    {isAdmin
                      ? 'Click "✨ Analyze" above to generate a coaching report using Gemini AI.'
                      : 'AI coaching analysis has not been run for this game yet.'}
                  </div>
                </div>
              );
            }

            // ── Processing skeleton ──────────────────────────────────────────
            if (status === 'processing') {
              return (
                <div style={{
                  background: 'var(--surface)', border: '1px solid rgba(168,85,247,0.2)',
                  borderRadius: '12px', padding: '32px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '28px' }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, rgba(168,85,247,0.3), rgba(249,115,22,0.3))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px',
                      animation: 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite',
                    }}>⚙️</div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '15px', color: 'var(--ink-1)', marginBottom: '4px' }}>
                        AI is analyzing game footage…
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--ink-3)' }}>
                        This usually takes a few minutes. The report will appear automatically.
                      </div>
                    </div>
                  </div>
                  {/* Skeleton bars */}
                  {[100, 75, 90, 60, 80].map((w, i) => (
                    <div key={i} style={{
                      height: '14px', borderRadius: '7px', marginBottom: '12px',
                      background: 'rgba(255,255,255,0.06)',
                      width: `${w}%`,
                      animation: `pulse 1.8s ease-in-out ${i * 0.15}s infinite`,
                    }} />
                  ))}
                </div>
              );
            }

            // ── Failed state ─────────────────────────────────────────────────
            if (status === 'failed') {
              return (
                <div style={{
                  background: 'var(--surface)', border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: '12px', padding: '28px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '22px' }}>⚠️</span>
                    <span style={{ fontWeight: 800, color: '#F87171', fontSize: '15px' }}>Analysis Failed</span>
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--ink-3)', fontFamily: 'monospace' }}>
                    {recording.analysis_error || 'Unknown error'}
                  </div>
                  {isAdmin && (
                    <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--ink-4)' }}>
                      Use the Re-analyze button above to try again.
                    </div>
                  )}
                </div>
              );
            }

            // ── Completed — render report ─────────────────────────────────────
            if (status === 'completed' && recording.analysis_report) {
              const report = recording.analysis_report;

              // Simple inline markdown renderer (handles ## h2, **bold**, numbered/bullet lists)
              const renderMarkdown = (text: string): React.ReactNode => {
                const lines = text.split('\n');
                const els: React.ReactNode[] = [];
                let key = 0;

                const renderInline = (s: string): React.ReactNode => {
                  const parts = s.split(/(\*\*[^*]+\*\*)/);
                  return parts.map((p, i) =>
                    p.startsWith('**') && p.endsWith('**')
                      ? <strong key={i} style={{ color: 'var(--ink-1)' }}>{p.slice(2, -2)}</strong>
                      : p
                  );
                };

                for (const line of lines) {
                  const trimmed = line.trim();
                  if (!trimmed) {
                    els.push(<div key={key++} style={{ height: '8px' }} />);
                  } else if (trimmed.startsWith('## ')) {
                    els.push(
                      <h3 key={key++} style={{
                        fontSize: '13px', fontWeight: 800, color: '#A78BFA',
                        textTransform: 'uppercase', letterSpacing: '1px',
                        marginTop: '24px', marginBottom: '10px', paddingBottom: '6px',
                        borderBottom: '1px solid rgba(168,85,247,0.2)',
                      }}>
                        {trimmed.slice(3)}
                      </h3>
                    );
                  } else if (trimmed.startsWith('### ')) {
                    els.push(
                      <h4 key={key++} style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink-1)', marginBottom: '6px' }}>
                        {trimmed.slice(4)}
                      </h4>
                    );
                  } else if (/^\d+\.\s/.test(trimmed)) {
                    els.push(
                      <div key={key++} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                        <span style={{ color: '#F97316', fontWeight: 800, flexShrink: 0, minWidth: '18px' }}>
                          {trimmed.match(/^(\d+)\./)?.[1]}.
                        </span>
                        <span style={{ fontSize: '14px', color: 'var(--ink-2)', lineHeight: 1.7 }}>
                          {renderInline(trimmed.replace(/^\d+\.\s*/, ''))}
                        </span>
                      </div>
                    );
                  } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
                    els.push(
                      <div key={key++} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'flex-start' }}>
                        <span style={{ color: '#F97316', flexShrink: 0 }}>•</span>
                        <span style={{ fontSize: '14px', color: 'var(--ink-2)', lineHeight: 1.7 }}>
                          {renderInline(trimmed.slice(2))}
                        </span>
                      </div>
                    );
                  } else {
                    els.push(
                      <p key={key++} style={{ fontSize: '14px', color: 'var(--ink-2)', lineHeight: 1.8, margin: '0 0 4px' }}>
                        {renderInline(trimmed)}
                      </p>
                    );
                  }
                }
                return els;
              };

              return (
                <div style={{
                  background: 'var(--surface)', border: '1px solid rgba(168,85,247,0.2)',
                  borderRadius: '12px', overflow: 'hidden',
                }}>
                  {/* Possession bar header */}
                  {recording.analysis_possession != null && (
                    <div style={{
                      padding: '20px 24px', borderBottom: '1px solid var(--hairline)',
                      background: 'rgba(168,85,247,0.05)',
                    }}>
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#A78BFA', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '10px' }}>
                        Estimated Possession
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink-1)', minWidth: '80px' }}>
                          {game.home_team.split(' ').slice(-1)[0]}
                        </span>
                        <div style={{ flex: 1, height: '10px', borderRadius: '5px', background: 'var(--hairline)', overflow: 'hidden', position: 'relative' }}>
                          <div style={{
                            width: `${recording.analysis_possession}%`,
                            height: '100%', borderRadius: '5px',
                            background: 'linear-gradient(to right, #A78BFA, #F97316)',
                            transition: 'width 0.8s ease',
                          }} />
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink-1)', minWidth: '80px', textAlign: 'right' }}>
                          {game.away_team.split(' ').slice(-1)[0]}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 800, color: '#A78BFA' }}>{recording.analysis_possession}%</span>
                        <span style={{ fontSize: '12px', fontWeight: 800, color: '#F97316' }}>{100 - recording.analysis_possession}%</span>
                      </div>
                    </div>
                  )}

                  {/* Full report */}
                  <div style={{ padding: '20px 24px' }}>
                    {renderMarkdown(report)}
                  </div>

                  {/* Footer */}
                  {recording.analysis_created_at && (
                    <div style={{
                      padding: '10px 24px', borderTop: '1px solid var(--hairline)',
                      fontSize: '11px', color: 'var(--ink-4)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px',
                    }}>
                      <span>🤖 Generated by Gemini 2.5 Pro</span>
                      <span>Analyzed {new Date(recording.analysis_created_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })()}
        </div>
      )}

      {/* Highlight upload modal */}
      {showHighlightUpload && game?.id && (
        <HighlightUploadModal
          gameId={game.id}
          onClose={() => setShowHighlightUpload(false)}
          onUploaded={() => { fetchVideos(game.id); }}
        />
      )}
    </div>
  );
};

export default Watch;
